import {
  Clock,
  Box3,
  Vector2,
  Euler,
  Raycaster,
  Vector3,
  PCFShadowMap,
  Color,
  PCFSoftShadowMap,
  VSMShadowMap,
  Box3Helper,
  MathUtils
} from "three";
import { Manager } from "./manager/Manager.js";
import { EventManagerCommand } from "./manager/EventManager.js";
import axios from "axios";
import { message } from "antd";
import Util from "./utils/utils.js";
import { BoxSelection } from "./lib/boxSelection.js"; // 框选逻辑
import { Create3DObject } from "./utils/Create3DObject.js"; // 创建基本几何体
import { MeasureUtil } from "./utils/MeasureUtil.js"; // 测量工具
import { CopyUtil } from "./utils/CopyUtil.js"; // 模型复制工具
import { SkyUtil } from "./utils/SkyUtil.js";
import { CloudUtil } from "./utils/CloudUtil.js";
import { CurveUtil } from "./utils/CurveUtil.js";
import { TerrainBrush } from "./utils/TerrainBrush.js";
import { RoadTool } from "./utils/RoadTool.js";
import {
  setMaterialInfo,
  setModelInfo,
  setSceneModelInfo,
  setMeasureList,
  setTreeList,
  setActiveTreeNodeId,
  setSkyInfo,
  setActiveView,
  setModelSetting,
  setModelArrange,
  setModelType,
  setModelAccuracy,
  setSceneModelSetting,
  setEditVisible,
  setTreeVisible,
  setSkyVisible,
  setSceneVisible,
  setViewVisible,
  setVrVisible,
  setMeasureVisible,
  setCreateModelVisible,
  setEditType,
} from "../store/module/app";
import store from "../store/index.js";
import { TransformCommand } from "./command/transformCommand.js";
import { CommonCommand } from "./command/commonCommand.js";
import { GLTFExporter } from "three/examples/jsm/exporters/GLTFExporter.js";

/**
 * 模型交互模式，由于不同功能面板需要与模型进行不同的交互，因此需要区分。
 * 默认情况下（None）无法选中模型（无法与模型进行交互），除非打开对应的面板。
 * 这些面板互斥，打开一个需要关闭其他面板。
 * 另外 创建模型面板 在创建模型后需要自动打开 编辑面板-场景模型 面板。
 */
const ClickMode = {
  None: 0,
  Measure: 1, // 测量模式
  SceneModel: 2, // 场景模型
  Terrain: 3, // 地形
  Way: 4, // 道路
  Material: 5, // 材质库
  Model: 6, // 模型库
  VR_Plane: 7, // VR设置 地面编辑
  VR_Position: 8, // VR设置 位置编辑
  CreateObject: 9, //创建基础模型
};

class Interface3D {
  constructor(el, options, data = []) {
    if (typeof el === "string") {
      this.el = document.getElementById(el);
    }
    this.options = options;
    this.enabled = false; //事件启用
    this.domElement = null;
    this.backgroud = false;
    this.mousedowning = null;
    this.ray = new Raycaster();

    this.useVR = true;
    this.useShadow = true; // 是否使用阴影

    // 创建加载蒙版容器
    this.loadingOverlay = document.createElement('div');
    this.loadingOverlay.style.position = 'absolute';
    this.loadingOverlay.style.top = '0';
    this.loadingOverlay.style.left = '0';
    this.loadingOverlay.style.width = '100%';
    this.loadingOverlay.style.height = '100%';
    this.loadingOverlay.style.zIndex = '999';
    this.loadingOverlay.style.display = 'flex';
    this.loadingOverlay.style.flexDirection = 'column';
    this.loadingOverlay.style.alignItems = 'center';
    this.loadingOverlay.style.justifyContent = 'center';
    this.loadingOverlay.style.fontFamily = 'Arial, sans-serif';
    // 添加毛玻璃效果和美化样式
    this.loadingOverlay.style.backdropFilter = 'blur(10px)';
    this.loadingOverlay.style.backgroundColor = 'rgba(255, 255, 255, 0.85)';
    this.loadingOverlay.style.boxShadow = 'inset 0 0 20px rgba(0, 0, 0, 0.1)';
    
    // 添加圆角和过渡效果
    this.loadingOverlay.style.borderRadius = '8px';
    this.loadingOverlay.style.transition = 'all 0.3s ease';

    // 创建内容容器，便于样式统一管理
    const contentContainer = document.createElement('div');
    contentContainer.style.padding = '30px';
    contentContainer.style.borderRadius = '12px';
    contentContainer.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
    contentContainer.style.boxShadow = '0 8px 32px rgba(31, 38, 135, 0.15)';
    contentContainer.style.border = '1px solid rgba(255, 255, 255, 0.18)';
    contentContainer.style.minWidth = '350px';
    contentContainer.style.textAlign = 'center';


    // 添加加载文本
    const loadingText = document.createElement('div');
    loadingText.textContent = '加载中...';
    loadingText.style.marginBottom = '20px';
    loadingText.style.fontSize = '22px';
    loadingText.style.fontWeight = '600';
    loadingText.style.color = '#2c3e50';
    loadingText.style.letterSpacing = '0.5px';
    contentContainer.appendChild(loadingText);
    
    // 添加HTML5进度条到蒙版
    this.progressBar = document.createElement('progress');
    this.progressBar.style.width = '100%';
    this.progressBar.style.height = '12px';
    this.progressBar.style.marginBottom = '15px';
    this.progressBar.style.borderRadius = '6px';
    this.progressBar.style.overflow = 'hidden';
    this.progressBar.style.border = 'none';
    this.progressBar.max = 100;
    this.progressBar.value = 0;
    // 自定义进度条样式
    this.progressBar.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
    
    // 添加样式规则以美化进度条
    const style = document.createElement('style');
    style.textContent = `
      #${this.progressBar.id}::-webkit-progress-bar {
        background-color: rgba(0, 0, 0, 0.1);
        border-radius: 6px;
      }
      #${this.progressBar.id}::-webkit-progress-value {
        background: linear-gradient(90deg, #4CAF50 0%, #8BC34A 100%);
        border-radius: 6px;
        transition: width 0.3s ease;
      }
      #${this.progressBar.id}::-moz-progress-bar {
        background: linear-gradient(90deg, #4CAF50 0%, #8BC34A 100%);
        border-radius: 6px;
      }
    `;
    document.head.appendChild(style);
    this.progressBarId = style;
    
    contentContainer.appendChild(this.progressBar);
    
    // 添加百分比文本
    this.percentText = document.createElement('div');
    this.percentText.textContent = '0%';
    this.percentText.style.fontSize = '18px';
    this.percentText.style.fontWeight = '500';
    this.percentText.style.color = '#4CAF50';
    contentContainer.appendChild(this.percentText);
    
    // 将内容容器添加到蒙版
    this.loadingOverlay.appendChild(contentContainer);
    
    // 确保蒙版被添加到容器中（取消注释）
    this.el.appendChild(this.loadingOverlay);


    this.manager = new Manager(this.el, options, {
      data: data,
      useVR: this.useVR,
    });
    this.manager.interface = this;
    this.clock = new Clock();

    this.manager.sceneManager.setUpdateProgress(this.updateProgress);
    this.manager.sceneManager.totalModelCount = this.options.modelInfo.length;
    // 开启阴影
    if (this.useShadow) {
      this.manager.sceneManager.renderer.shadowMap.enabled = true;
      this.manager.sceneManager.renderer.shadowMap.type = PCFSoftShadowMap; //BasicShadowMap; // PCFShadowMap;//VSMShadowMap//PCFSoftShadowMap;
    }

    this.clickMode = ClickMode.None; //ClickMode.None;

    // 框选工具
    this.boxSelection = new BoxSelection(this.manager.sceneManager);
    this.boxSelection.changeMode(
      this.clickMode === ClickMode.SceneModel ? true : false
    );
    this.boxSelection.setHighlightCB((objs) => {
      this.onBoxSelectCB(objs);
    });

    // 测量工具
    this.measureUtil = new MeasureUtil(
      this.manager.sceneManager.scene,
      this.manager.sceneManager.activeCamera,
      this.manager
    );
    this.measureUtil.setUpdateMeasureRecordCB((records) => {
      if (this.manager.isInVR()) {
        this.manager.vrManager.updateMeasureRecords(records);
      } else {
        store.dispatch(setMeasureList(records));
      }
    });

    // 创建基本几何体对象
    this.create3DObject = new Create3DObject();

    // 模型复制工具
    this.copyUtil = new CopyUtil();
    this.copyUtil.setGetModelCB((nodeId) => {
      const model = this.getModelByNodeId(nodeId);
      return model;
    });

    // 地形笔刷工具
    this.terrainBrush = new TerrainBrush(this.manager);
    
    // 初始化道路工具
    this.roadTool = new RoadTool(this.manager);

    this.copyArgs = {
      row: 1,
      col: 1,
      dis: 1,
    };

    // 曲线编辑工具
    this.curveUtil = new CurveUtil(
      this.manager.sceneManager.scene,
      this.manager
    );
    this.modelLibArgs = {
      modelLibType: "matrix", // 模型库编辑类型 "matrix"/"curve"
      row: 1,
      col: 1,
      dis: 1,
      random: false,
      curveType: "curve",
      accuracy: { value: "middle", label: "中" }, // 模型精度
    };
    this.dis2Camera = 10; // 模型库矩阵放置时光标未悬停在模型上时拖拽模型距离相机的距离
    this.modelRandomScaleOffset = 0.1; // 模型库放置模型时缩放的阈值
    this.curveUtil.setType(this.modelLibArgs.curveType);
    // 初始化前端模型库数据
    store.dispatch(
      setModelInfo({
        name: "",
        image: "/defaultMat.jpg",
        isSelect: false,
      })
    );

    this.skyUtil = new SkyUtil(
      this.manager.sceneManager.scene,
      this.manager.sceneManager.renderer,
      this.useShadow
    );

    this.cloudUtil = new CloudUtil(
      this.manager.sceneManager.scene,
      this.manager.sceneManager.renderer,
      this.manager.sceneManager.activeCamera,
      this.el
    );

    this.update = this.update.bind(this);
    // 可进入VR
    if (!this.useVR) {
      this.update();
    } else {
      this.manager.sceneManager.setVRLoop((timestamp, xrFrame) => {
        if (xrFrame) {
          this.manager.vrManager.setVRCameraHeight(xrFrame);
        }
        this.update();
      });
    }

    this.restoreData();
  }

  update() {
    const dt = this.clock.getDelta();
    this.manager.update(dt);

    if (this.measureUtil) {
      this.measureUtil.update();
    }

    if (this.skyUtil) {
      this.skyUtil.update(dt);
    }

    if (this.cloudUtil) {
      this.cloudUtil.update(dt);
    }

    if (!this.useVR) {
      this.loopId = requestAnimationFrame(this.update);
    }
  }

  clear() {
    if (this.loopId) cancelAnimationFrame(this.loopId);
    this.removeListener();
    this.manager.clear();
    if (this.create3DObject) this.create3DObject.clear();
    if (this.copyUtil) this.copyUtil.clear();
    if (this.curveUtil) this.curveUtil.clear();
    if (this.boxSelection) this.boxSelection.clear();

    // 清理加载蒙版和进度条
    if (this.loadingOverlay && this.loadingOverlay.parentNode) {
      this.loadingOverlay.parentNode.removeChild(this.loadingOverlay);
      this.loadingOverlay = null;
    }
    this.progressBar = null;
    this.percentText = null;

    // 清理添加的样式规则
    if (this.progressBarId && this.progressBarId.parentNode) {
      this.progressBarId.parentNode.removeChild(this.progressBarId);
      this.progressBarId = null;
    }
  }

  /**
   * 从数据库还原
   * @returns
   */
  restoreData() {
    if (!this.options) return;
    for (let i = 0; i < this.options.modelInfo.length; i++) {
      axios.get(this.options.modelInfo[i].configurl).then((res) => {
        //获取模型的服务器的数据
        console.log("获取的模型数据库的信息", res, this.options);

        // 保存过的工程
        if (res.data && (res.data.modelInfos || res.data.actives)) {
          // 旧版本
          if (res.data.actives) {
            this.options.modelInfo[i].actives = res.data.actives;
          } else
            this.options.modelInfos = res.data.modelInfos
              ? res.data.modelInfos
              : [];
          this.options.materialInfos = res.data.materialInfos
            ? res.data.materialInfos
            : [];

          //console.log(res)
          if (i === 0) {
            this.options.jsondata = {};
            this.options.jsondata.lightInfos = res.data.lightInfos
              ? res.data.lightInfos
              : {};
            this.options.jsondata.vr = res.data.vr;
            if (res.data.backgroud === true || res.data.backgroud === false) {
              this.backgroud = res.data.backgroud;
            }
            // 恢复基本几何体
            if (res.data.object3Ds) {
              this.options.jsondata.object3Ds = res.data.object3Ds;
            }

            // 恢复复制体
            if (res.data.copyObjs) {
              this.options.jsondata.copyObjs = res.data.copyObjs;
            }
          }

          this.manager.sceneManager.totalModelCount =
            this.options.modelInfos.length;

          console.log(this.options);
          for (var k = 0; k < this.options.modelInfos.length; k++) {
            // 图纸生成的模型
            if (this.options.modelInfos[k].singleStates) {
              // 图纸id不一致，表示是新上传的图纸
              if (
                this.options.modelInfos[k].updateTime !==
                this.options.modelInfo[0].updatedAt
              ) {
                console.log(
                  "新上传的图纸",
                  this.options.modelInfos[k].updateTime,
                  this.options.modelInfo[0].updatedAt
                );

                this.options.modelInfos[k].singleStates = null;
                this.options.jsondata.vr = null;
              }
            }

            this.loadModels(this.options.modelInfos[k], k, () => {
              //所有模型加载完成
              this.modelsLoadCompleted();
            });
          }
        } else {
          this.manager.sceneManager.totalModelCount = 1;
          // 旧版本,或者未曾保存过的工程
          this.loadModels(this.options.modelInfo[i], i, () => {
            //所有模型加载完成
            this.modelsLoadCompleted();
          });
        }
      });
    }
  }

  modelsLoadCompleted() {
    console.log("所有模型加载完成");
    this.domElement = this.manager.sceneManager.renderer.domElement;
    this.enabled = true;
    this.addListener();
    window.viewer = this.manager.sceneManager;
    window.editor = this;
    //this.fullScreen();

    if (this.options.jsondata) {
      // 灯光数据
      if (this.options.jsondata.lightInfos) {
        const infos = this.options.jsondata.lightInfos;
        if (infos.ambientColor) this.setAmbientColor(infos.ambientColor);
        if (infos.ambientIntensity)
          this.setAmbientIntensity(infos.ambientIntensity);
        if (infos.fogColor) this.setFogColor(infos.fogColor);
        if (infos.fogDensity) this.setFogDensity(infos.fogDensity);
        if (infos.sunAngle) this.setSunAngle(infos.sunAngle);
      }
      //this.manager.lightAndShadowManager.setInitData();

      // 基本几何体数据
      if (this.options.jsondata.object3Ds && this.create3DObject) {
        this.create3DObject.restore(this.options.jsondata.object3Ds, (obj) => {
          this.manager.sceneManager.scene.add(obj);
          this.manager.sceneManager.singleModels.push(obj);
        });
      }

      // 复制体
      if (this.options.jsondata.copyObjs && this.copyUtil) {
        this.copyUtil.restore(this.options.jsondata.copyObjs, (obj) => {
          this.manager.sceneManager.scene.add(obj);
          this.manager.sceneManager.singleModels.push(obj);
        });
      }
    }

    // 材质数据
    this.manager.materialManager.setInitData(this.options.materialInfos);

    this.manager.sceneManager.centerScene();

    if (this.manager.eventManager)
      this.manager.eventManager.dispach(EventManagerCommand.modelAllLoaded);

    if (this.options.jsondata)
      this.manager.vrSettingManager.setInitData(this.options.jsondata.vr);

    // 设置相机初始位置
    // 先检测是否在VR设置中设置了初始位置
    if (
      this.options.jsondata &&
      this.options.jsondata.vr &&
      this.options.jsondata.vr.position &&
      this.options.jsondata.vr.position.length > 0
    ) {
      let p = this.options.jsondata.vr.position[0];
      this.manager.sceneManager.activeCamera.position.set(p.x, p.y + 1.5, p.z);
    }
    // 将相机放到模型中心，地面上
    else {
      let box = new Box3();
      const singleModels = this.manager.sceneManager.singleModels.filter(
        (element) => !element._delete && element.visible
      );
      for (var i = 0; i < singleModels.length; i++) {
        box.expandByObject(singleModels[i]);
      }

      let center = box.getCenter(new Vector3());
      let size = box.getSize(new Vector3());

      const src = new Vector3(center.x, center.y + size.y, center.z);
      const dir = center.clone().sub(src).normalize();
      const ray = new Raycaster(src, dir);
      let objs = [];
      // if (this.manager.vrSettingManager.planeNodes)
      //   objs = objs.concat(this.manager.vrSettingManager.planeNodes);
      objs = objs.concat(singleModels);
      if (this.manager.sceneManager.floor)
        objs.push(this.manager.sceneManager.floor);

      if (objs.length === 0) {
        this.manager.sceneManager.activeCamera.position.set(0, 1.5, 0);
      } else {
        const intersects = ray.intersectObjects(objs, true);

        if (intersects && intersects.length > 0) {
          console.log("射线检测的初始位置")
          this.manager.sceneManager.activeCamera.position.set(
            intersects[0].point.x,
            intersects[0].point.y + 50,
            intersects[0].point.z
          );

          this.manager.sceneManager.activeCamera.rotateY(MathUtils.degToRad(-45));
          this.manager.sceneManager.activeCamera.rotateX(MathUtils.degToRad(-30));
        } else {
          console.log("没有射线检测的初始位置")
          this.manager.sceneManager.activeCamera.position.set(0, 50, 0);

          this.manager.sceneManager.activeCamera.rotateY(MathUtils.degToRad(-45));
          this.manager.sceneManager.activeCamera.rotateX(MathUtils.degToRad(-30));
        }
      }
    }

    store.dispatch(setTreeList(this.getMeshTree()));

    if (this.skyUtil) {
      this.skyUtil.updateShadowMapSize(this.manager.sceneManager.singleModels);
    }

    this.updateProgress(100); //等材质树初始化完成再显示前端

    // 开启历史记录
    setTimeout(() => {
      this.manager.enableHistory(true);
      this.sunAngleSrcValue = undefined;
      this.ambientIntensitySrcValue = undefined;
      this.fogDensitySrcValue = undefined;

      this.manager.vrManager.showBtn = true;
    }, 1000);
  }

  /**
   * 对节点对应的模型的可视性进行设置
   * @param {*} nodeid 节点的数据
   * @param {*} key true
   */
  getNodeVisibleorUnvisible(nodeid, key) {
    //console.log(nodeid);

    const model = this.getModelByNodeId(nodeid);
    if (model) {
      const state = {
        obj: model,
        oldState: model.visible,
        newState: key,
      };
      const cmd = new CommonCommand(
        state,
        (arg) => {
          if (arg && arg.obj) {
            arg.obj.visible = arg.oldState;
            //  通知前端更新结构树
            store.dispatch(setTreeList(this.getMeshTree()));
          }
        },
        (arg) => {
          if (arg && arg.obj) {
            arg.obj.visible = arg.newState;
            // 通知前端更新结构树
            store.dispatch(setTreeList(this.getMeshTree()));
          }
        }
      );
      this.manager.addHistory(cmd);

      model.visible = key;
    }
  }

  /**
   * 加载模型
   * @param {*} urls
   */
  async loadModels(options, index, loadCompletedCB) {
    this.manager.sceneManager
      .load(options, index, loadCompletedCB) //加载服务器模型
      .then(async (gltf) => {
        const scene = gltf.scene || gltf.scenes[0];
        // console.log(scene);

        //恢复节点显隐状态
        // 旧版本 ,新版本等构建材质树后一起恢复
        if (options.actives) {
          let active = options.actives;
          if (active) {
            let index = 0;
            this.traverse(scene, (node) => {
              node.visible = active[index++];
            });
          }
        }
      })
      .catch((err) => {
        console.log("加载模型报错：", err);
      });
  }

  /**
   * 根据不同类型创建基本几何体
   * @param {*} type
   * @param {*} cb 回调函数，将创建的对象传递出去
   */
  createBasicObject(type, cb) {
    if (this.create3DObject) {
      this.create3DObject.createObject(
        type,
        new Vector3(0, 0, 0),
        [0, 0, 0, 1],
        new Vector3(1, 1, 1),
        null,
        null,
        (obj) => {
          // 历史记录相关
          {
            const state = {
              oldState: this.currentOperateModel,
              newState: obj,
            };
            const cmd = new CommonCommand(
              state,
              (arg) => {
                if (arg && arg.oldState) {
                  if (this.currentOperateModel)
                    this.manager.sceneManager.scene.remove(
                      this.currentOperateModel
                    );
                  this.currentOperateModel = arg.oldState;
                  if (this.currentOperateModel)
                    this.manager.sceneManager.scene.add(
                      this.currentOperateModel
                    );
                } else {
                  if (this.currentOperateModel)
                    this.manager.sceneManager.scene.remove(
                      this.currentOperateModel
                    );
                  this.currentOperateModel = null;
                }
              },
              (arg) => {
                if (arg && arg.newState) {
                  if (this.currentOperateModel)
                    this.manager.sceneManager.scene.remove(
                      this.currentOperateModel
                    );
                  this.currentOperateModel = arg.newState;
                  if (this.currentOperateModel)
                    this.manager.sceneManager.scene.add(
                      this.currentOperateModel
                    );
                } else {
                  if (this.currentOperateModel)
                    this.manager.sceneManager.scene.remove(
                      this.currentOperateModel
                    );
                  this.currentOperateModel = null;
                }
              }
            );
            this.manager.addHistory(cmd);
          }

          if (this.currentOperateModel)
            this.currentOperateModel.removeFromParent();

          this.currentOperateModel = obj;
          this.manager.sceneManager.scene.add(obj);

          // 计算模型底面中心与模型位置的偏移
          const box = new Box3();
          box.expandByObject(obj);
          const center = box.getCenter(new Vector3());
          const size = box.getSize(new Vector3());
          obj._offset = obj.position
            .clone()
            .sub(new Vector3(center.x, center.y - size.y / 2, center.z));

          if (this.manager.isInVR()) {
            this.manager.vrManager.makeSystemTip("选择成功");
          } else {
            message.info("选择成功");
          }
        }
      );
    }
  }

  /**
   * 对vr模型的旋转和地板和位置的开关设定
   * @param {*} order "rotate" "plane" "position" 旋转 地板设定 位置设定
   * @param {*} key  true false
   */
  editorOnoff(order, key = false, needHistory = true) {
    //console.log("传递的位置", order, key);
    if (order === "plane") {
      //如果是设置地板的按钮
      this.manager.vrSettingManager.BingerPlaneSet(key, needHistory);
    } else if (order === "position") {
      //如果是对模型位置设定的按钮
      this.manager.vrSettingManager.BingerPostionSet(key, needHistory);
      //console.log("传递的位置", key);
    }
  }
  /**
   * 对模型的地板设定和位置设定的确认和删除
   * @param {*} order "plane" "postion"地板设定 位置设定  "color"地板的颜色设定 如果是颜色 key只能传递颜色数据
   * @param {*} key  "confirm" "delect"  确认 删除
   */
  editorConfirmAndDelect(order, key) {
    if (order === "plane") {
      // 如果是对地板的确认和删除
      this.manager.vrSettingManager.editorPlane(key);
    } else if (order === "position") {
      //如果是对位置的确认和删除
      this.manager.vrSettingManager.editorPostion(key);
      //console.log("传递的位置",key)
    } else if (order === "color") {
      //如果是对地板的颜色的设定
      this.manager.vrSettingManager.editorPlane(order, key);
      //console.log("颜色传读",order,key)
    }
  }

  /**
   * 对VR进行设置
   * @param {*} key true false
   */
  VRsetKey(key, needHistory = true) {
    this.manager.vrSettingManager.BingerModelSet(key, needHistory);
  }

  addListener() {
    this.onKeyDownFunc = this.onKeyDown.bind(this);
    this.onKeyUpFunc = this.onKeyUp.bind(this);
    this.onPointerDownFunc = this.onPointerDown.bind(this);
    this.onPointerUpFunc = this.onPointerUp.bind(this);
    this.onPointDropFunc = this.onPointDrop.bind(this);
    this.onPointerMoveFunc = this.onPointerMove.bind(this);
    //this.onPointerLeaveFunc = this.onPointerLeave.bind(this);
    this.onDBclickFunc = this.onDBclick.bind(this);

    this.manager.eventManager.addEventListener(
      EventManagerCommand.keyDown,
      this.onKeyDownFunc
    );
    this.manager.eventManager.addEventListener(
      EventManagerCommand.keyUp,
      this.onKeyUpFunc
    );
    this.manager.eventManager.addEventListener(
      EventManagerCommand.pointerDown,
      this.onPointerDownFunc
    );
    this.manager.eventManager.addEventListener(
      EventManagerCommand.pointerUp,
      this.onPointerUpFunc
    );
    this.manager.eventManager.addEventListener(
      EventManagerCommand.pointerMove,
      this.onPointerMoveFunc
    );
    
    // 添加鼠标离开事件监听
    /*if (this.domElement) {
      this.domElement.addEventListener('mouseleave', this.onPointerLeaveFunc);
    }*/

    this.manager.eventManager.addEventListener(
      EventManagerCommand.touchstart,
      this.onPointerUpFunc
    );
    this.manager.eventManager.addEventListener(
      EventManagerCommand.touchend,
      this.onPointerUpFunc
    );
    this.manager.eventManager.addEventListener(
      EventManagerCommand.touchcancel,
      this.onPointerUpFunc
    );
    this.manager.eventManager.addEventListener(
      EventManagerCommand.touchleave,
      this.onPointerUpFunc
    );
    this.manager.eventManager.addEventListener(
      EventManagerCommand.dblClick,
      this.onDBclickFunc
    );

    //document.addEventListener("drop", this.onPointDropFunc, false);
    this.manager.eventManager.addEventListener(
      EventManagerCommand.mouseDrop,
      this.onPointDropFunc
    );
  }

  removeListener() {
    this.manager.eventManager.removeEventListener(
      EventManagerCommand.keyDown,
      this.onKeyDownFunc
    );
    this.manager.eventManager.removeEventListener(
      EventManagerCommand.keyUp,
      this.onKeyUpFunc
    );
    this.manager.eventManager.removeEventListener(
      EventManagerCommand.pointerDown,
      this.onPointerDownFunc
    );

    this.manager.eventManager.removeEventListener(
      EventManagerCommand.pointerUp,
      this.onPointerUpFunc
    );

    this.manager.eventManager.removeEventListener(
      EventManagerCommand.pointerMove,
      this.onPointerMoveFunc
    );
    
    // 移除鼠标离开事件监听
    /*if (this.domElement) {
      this.domElement.removeEventListener('mouseleave', this.onPointerLeaveFunc);
    }*/
    

    this.manager.eventManager.removeEventListener(
      EventManagerCommand.touchstart,
      this.onPointerUpFunc
    );
    this.manager.eventManager.removeEventListener(
      EventManagerCommand.touchend,
      this.onPointerUpFunc
    );
    this.manager.eventManager.removeEventListener(
      EventManagerCommand.touchcancel,
      this.onPointerUpFunc
    );
    this.manager.eventManager.removeEventListener(
      EventManagerCommand.touchleave,
      this.onPointerUpFunc
    );
    this.manager.eventManager.removeEventListener(
      EventManagerCommand.dblClick,
      this.onDBclickFunc
    );

    //document.removeEventListener("drop", this.onPointDropFunc, false);
    this.manager.eventManager.removeEventListener(
      EventManagerCommand.mouseDrop,
      this.onPointDropFunc
    );

    this.onKeyDownFunc = null;
    this.onKeyUpFunc = null;
    this.onPointerDownFunc = null;
    this.onPointerUpFunc = null;
    this.onPointDropFunc = null;
    this.onDBclickFunc = null;
    this.onPointerMoveFunc = null;
    this.onPointerLeaveFunc = null;
  }

  onKeyUp = (event) => {
    event = Array.isArray(event) ? event[0] : event;
  };

  onPointerLeave = (event) => {
    // 当鼠标离开画布时，隐藏地形笔刷指示器
    if (this.clickMode === ClickMode.Terrain && this.terrainBrush) {
      this.terrainBrush.hideBrushIndicator();
    }
  };

  onKeyDown = (event) => {
    event = Array.isArray(event) ? event[0] : event;

    switch (event.keyCode) {
      case 49: //1
        //if (this.cloudUtil) this.cloudUtil.createCloud();
        //this.createBasicObject(0);
        // this.selectModelibItem(
        //   "/assets/scene.gltf", //
        //   "/assets/texture0.jpg",
        //   "11111",
        //   "tree"
        // );
        break;
      case 50: //2
        break;
      case 51: //3
        //this.setupView("right");
        break;
      case 52: //4
        //this.copyObj(2, 2);
        break;
      case 53: //5
        //this.manager.clippingManager.changeClipAxis(0, "-X");
        break;

      case 46: //delete  删除当前选中的模型
        if (this.clickMode === ClickMode.SceneModel) this.deleteCurrentModel();
        break;
      default:
        break;
    }
  };

  onPointerDown = (event) => {
    event = Array.isArray(event) ? event[0] : event;

    if (!this.enabled) return;
    this.mousedowning = new Vector2(event.clientX, event.clientY);

    // 地形笔刷模式处理
    if (this.clickMode === ClickMode.Terrain && this.terrainBrush) {
      this.terrainBrush.onPointerDown(event);
    }
    // 处理道路工具的点击事件
    else if (this.roadTool) {
      this.roadTool.onPointerDown(event);
    }
  };

  // 左键双击到模型时将相机移动到近处
  onDBclick = (event) => {
    // 道路工具模式处理
    if (this.clickMode === ClickMode.Way && this.roadTool) {
      this.roadTool.onDoubleClick(event);
      return;
    }
    
    // 顶视图或其他视图不进行操作
    if (!this.manager.sceneManager.activeCamera.isPerspectiveCamera) {
      return;
    }

    event = Array.isArray(event) ? event[0] : event;

    const singleModels = this.manager.sceneManager.singleModels.filter(
      (element) => !element._delete && element.visible
    );

    if (!this.enabled || singleModels <= 0) return;

    let pointer = Util.get3DPointer(event, this.domElement);
    if (pointer.button === 0 || pointer.button === undefined) {
      this.ray.setFromCamera(pointer, this.manager.sceneManager.activeCamera);

      let dataObject = [];
      dataObject = dataObject.concat(singleModels);
      var planeIntersects = this.ray.intersectObjects(dataObject, true); //所有的模型物体
      var planeIntersect = null;
      for (var i = 0; i < planeIntersects.length; i++) {
        if (
          planeIntersects[i].object &&
          planeIntersects[i].object.material &&
          planeIntersects[i].object.material.visible
        ) {
          planeIntersect = planeIntersects[i];
          //console.log(planeIntersect)
          break;
        }
      }
      if (planeIntersect) {
        const pos = planeIntersect.point;
        const dir = this.manager.sceneManager.activeCamera.position
          .clone()
          .sub(pos)
          .normalize();
        const targetPos = pos.clone().add(dir.clone().multiplyScalar(1)); // 距离目标点1.5米
        this.manager.sceneManager.lerpCamera(targetPos);
      }
    }
  };

  onPointerUp = (event) => {
    event = Array.isArray(event) ? event[0] : event;
    this.terrainBrush.onPointerUp(event);

    if (!this.enabled) return;

    if (!this.mousedowning) {
      return;
    }

    if (this.mousedowning) {
      let distance = this.mousedowning.distanceTo(
        new Vector2(event.clientX, event.clientY)
      );
      this.mousedowning = false;

      // mouse is move
      if (distance > 1) {
        return;
      }
    }

    let pointer = Util.get3DPointer(event, this.domElement);
    // 左键单击
    if (pointer.button === 0 || pointer.button === undefined) {
      this.ray.setFromCamera(pointer, this.manager.sceneManager.activeCamera);

      let dataObject = [];
      const singleModels = this.manager.sceneManager.singleModels.filter(
        (element) => !element._delete && element.visible
      );
      dataObject = dataObject.concat(singleModels);
      // vr设置中位置的设定
      if (this.clickMode === ClickMode.VR_Position) {
        if (this.manager.vrSettingManager.cloneModels != null) {
          dataObject = dataObject.concat(
            this.manager.vrSettingManager.cloneModels
          );
        }
      }
      // 测量或模型库
      else if (
        this.clickMode === ClickMode.Measure ||
        this.clickMode === ClickMode.Model
      ) {
        dataObject.push(this.manager.sceneManager.floor);
      }

      var planeIntersects = this.ray.intersectObjects(dataObject, true); //所有的模型物体
      var planeIntersect = null;
      for (var i = 0; i < planeIntersects.length; i++) {
        if (
          planeIntersects[i].object &&
          planeIntersects[i].object.material &&
          planeIntersects[i].object.material.visible
        ) {
          planeIntersect = planeIntersects[i];
          //console.log(planeIntersect)
          break;
        }
      }

      //console.log("点击的模型的内容", planeIntersect);

      // 地形笔刷模式处理
      if (this.clickMode === ClickMode.Terrain && this.terrainBrush) {
        this.terrainBrush.onPointerUp(event);
        return;
      }

      // 点击到对象
      if (planeIntersect) {
        this.onMeshLeftClick(planeIntersect, event);
      }
      // 未点击到对象
      else {
        this.onClickNull(event);
      }
    }
    // 右键单击
    else if (pointer.button === 2) {
      // 右键单击时如果处于模型库模式并且当前有选择的模型
      if (this.clickMode === ClickMode.Model) {
        // 停止模型放置状态
        // 曲线
        if (this.modelLibArgs.modelLibType === "curve") {
          if (this.curveUtil.enabled) {
            this.curveUtil.stopDraw();

            if (this.currentOperateModel) {
              this._generageModelByCurve();
            }

            // 历史记录相关
            {
              const cmd = new CommonCommand(
                null,
                () => {
                  this.curveUtil.enabled = true;
                  if (this.currentOperateModel) {
                    this._clearCurveModel();
                  }
                },
                () => {
                  this.curveUtil.stopDraw();

                  if (this.currentOperateModel) {
                    this._generageModelByCurve();
                  }
                }
              );
              this.manager.addHistory(cmd);
            }
          } else {
            if (this.currentOperateModel) {
              let oldModel = this.currentOperateModel;
              this.manager.materialManager.deleteNode(this.currentOperateModel);
              this.currentOperateModel = null;

              store.dispatch(
                setModelInfo({
                  name: "",
                  image: "/defaultMat.jpg",
                  isSelect: false,
                })
              );

              // 历史记录相关
              {
                const state = {
                  obj: oldModel,
                };
                const cmd = new CommonCommand(
                  state,
                  (arg) => {
                    if (arg && arg.obj) {
                      this.currentOperateModel = arg.obj;
                      this.manager.materialManager.restoreNode(
                        this.currentOperateModel
                      );
                      this.manager.sceneManager.scene.add(
                        this.currentOperateModel
                      );

                      store.dispatch(
                        setModelInfo(
                          this.currentOperateModel.userData._modellibData
                        )
                      );
                    }
                  },
                  (arg) => {
                    this.manager.materialManager.deleteNode(
                      this.currentOperateModel
                    );
                    this.currentOperateModel = null;

                    store.dispatch(
                      setModelInfo({
                        name: "",
                        image: "/defaultMat.jpg",
                        isSelect: false,
                      })
                    );
                  }
                );
                this.manager.addHistory(cmd);
              }
            }
          }
        }
        // 矩阵
        else {
          if (this.currentOperateModel) {
            // 历史记录相关
            {
              const state = {
                obj: this.currentOperateModel,
              };
              const cmd = new CommonCommand(
                state,
                (arg) => {
                  if (arg && arg.obj) {
                    this.currentOperateModel = arg.obj;
                    this.manager.materialManager.restoreNode(
                      this.currentOperateModel
                    );
                    store.dispatch(
                      setModelInfo(
                        this.currentOperateModel.userData._modellibData
                      )
                    );
                  }
                },
                (arg) => {
                  this.manager.materialManager.deleteNode(
                    this.currentOperateModel
                  );
                  this.currentOperateModel = null;

                  store.dispatch(
                    setModelInfo({
                      name: "",
                      image: "/defaultMat.jpg",
                      isSelect: false,
                    })
                  );
                }
              );
              this.manager.addHistory(cmd);
            }

            this.manager.materialManager.deleteNode(this.currentOperateModel);
            this.currentOperateModel = null;

            store.dispatch(
              setModelInfo({
                name: "",
                image: "/defaultMat.jpg",
                isSelect: false,
              })
            );
          }
        }
      } else if (this.clickMode === ClickMode.CreateObject) {
        if (this.currentOperateModel) {
          // 历史记录相关
          {
            const state = {
              obj: this.currentOperateModel,
            };
            const cmd = new CommonCommand(
              state,
              (arg) => {
                if (arg && arg.obj) {
                  this.manager.sceneManager.scene.add(arg.obj);
                  this.currentOperateModel = arg.obj;
                }
              },
              (arg) => {
                this.currentOperateModel.removeFromParent();
                this.currentOperateModel = null;
              }
            );
            this.manager.addHistory(cmd);
          }

          this.currentOperateModel.removeFromParent();
          this.currentOperateModel = null;
        }
      }
    }
  };

  /**
   * 主要用于从材质列表中拖拽替换材质
   * @param {*} event
   * @returns
   */
  onPointDrop = (event) => {
    console.log("拖动");
  };

  onPointerMove = (event) => {
    if (!this.enabled) return;
    event = Array.isArray(event) ? event[0] : event;

    // 地形笔刷模式处理
    if (this.clickMode === ClickMode.Terrain && this.terrainBrush) {
      this.terrainBrush.onPointerMove(event);
      return;
    }
    
    if (this.clickMode === ClickMode.Way && this.roadTool) {
      this.roadTool.onPointerMove(event);
      return;
    }

    if (this.clickMode === ClickMode.CreateObject && !this.currentOperateModel)
      return;
    if (
      this.clickMode === ClickMode.Model &&
      this.modelLibArgs.modelLibType === "matrix" &&
      !this.currentOperateModel
    )
      return;

    if (
      this.clickMode === ClickMode.Model ||
      this.clickMode === ClickMode.CreateObject
    ) {
      let pointer = Util.get3DPointer(event, this.domElement);
      this.ray.setFromCamera(pointer, this.manager.sceneManager.activeCamera);

      let dataObject = [this.manager.sceneManager.floor];
      const singleModels = this.manager.sceneManager.singleModels.filter(
        (element) => !element._delete && element.visible && (element.name.indexOf("路面") != -1 || element.name.indexOf("地形") != -1)
      );
  
      dataObject = dataObject.concat(singleModels);

      // 测量或模型库
      if (this.clickMode === ClickMode.Model) {
        // 曲线编辑模式
        if (this.modelLibArgs.modelLibType === "curve") {
          if (this.curveUtil && this.curveUtil.enabled) {
            var planeIntersects = this.ray.intersectObjects(dataObject, true); //所有的模型物体
            if (planeIntersects && planeIntersects.length > 0) {
              this.curveUtil.onPointerMove(planeIntersects[0].point.clone());
            }
          }
        }
        // 矩阵编辑模式
        else {
          if (this.currentOperateModel) {
            var planeIntersects = this.ray.intersectObjects(dataObject, true); //所有的模型物体
            if (planeIntersects && planeIntersects.length > 0) {
              let target = planeIntersects[0];
              let point = target.point.clone();
              // 暂时先将模型放置在点击点的位置
              if (this.currentOperateModel._offset) {
                this.currentOperateModel.position.copy(
                  point.add(this.currentOperateModel._offset)
                );
              } else this.currentOperateModel.position.copy(point);
            }
            // 没有点击到模型
            else {
              const point = Util.screenToWorld(
                event,
                this.domElement,
                this.manager.sceneManager.activeCamera,
                this.dis2Camera
              );
              if (this.currentOperateModel._offset) {
                this.currentOperateModel.position.copy(
                  point.add(this.currentOperateModel._offset)
                );
              } else {
                this.currentOperateModel.position.copy(src);
              }
            }
          }
        }
      }
      // 放置基础模型
      else if (this.clickMode === ClickMode.CreateObject) {
        if (this.currentOperateModel) {
          var planeIntersects = this.ray.intersectObjects(dataObject, true); //所有的模型物体
          if (planeIntersects && planeIntersects.length > 0) {
            let target = planeIntersects[0];
            let point = target.point.clone();
            // 暂时先将模型放置在点击点的位置
            if (this.currentOperateModel._offset) {
              this.currentOperateModel.position.copy(
                point.add(this.currentOperateModel._offset)
              );
            } else this.currentOperateModel.position.copy(point);
          }
          // 没有点击到模型
          else {
            const point = Util.screenToWorld(
              event,
              this.domElement,
              this.manager.sceneManager.activeCamera,
              this.dis2Camera
            );
            if (this.currentOperateModel._offset) {
              this.currentOperateModel.position.copy(
                point.add(this.currentOperateModel._offset)
              );
            } else {
              this.currentOperateModel.position.copy(src);
            }
          }
        }
      }
    }
  };

  // 模型点击事件统一响应
  onMeshLeftClick = (intersect, event) => {
    if (!intersect) return;
    let { object, point } = intersect;

    switch (this.clickMode) {
      case ClickMode.Measure: // 测量
        this.measureUtil.clickMesh(point.clone());
        break;
      case ClickMode.VR_Plane: // 地板设定
        this.manager.vrSettingManager.editorPlane("set", object);
        break;
      case ClickMode.VR_Position: // 位置设定
        this.manager.vrSettingManager.editorPostion("set", intersect);
        break;
      case ClickMode.SceneModel: // 场景模型
        if (object && object._root) {
          this.clickMeshToHierarchy([object._root.nodeId]);
        }
        break;
      case ClickMode.Terrain: // 地形
        // TODO
        break;
      case ClickMode.Material: // 材质库 选中后将其材质闪烁，可以直接修改选中的材质
        this.onSelectMatForMatlib(object.material);
        break;
      case ClickMode.Model: // 模型库 不能选中场景中的模型，但可以在加载模型库模型后拖拽滑动在场景模型表面
        this.onModellibLeftClick(point);
        break;
      case ClickMode.Way: // 道路
        break;
      case ClickMode.CreateObject: // 放置基础模型
        this.onCreateObjectLeftClick();
        break;
      default:
        break;
    }
  };

  /**
   * 未点击到对象
   * @param {*} event
   */
  onClickNull(event) {
    switch (this.clickMode) {
      case ClickMode.Measure: // 测量
        break;
      case ClickMode.VR_Plane: // 地板设定
        break;
      case ClickMode.VR_Position: // 位置设定
        break;
      case ClickMode.SceneModel: // 场景模型
        this.clickMeshToHierarchy(null);
        this.pointNull();
        break;
      case ClickMode.Terrain: // 地形
        // TODO
        break;
      case ClickMode.Material: // 材质库 选中后将其材质闪烁，可以直接修改选中的材质
        this.onSelectMatForMatlib();
        break;
      case ClickMode.Model: // 模型库 不能选中场景中的模型，但可以在加载模型库模型后拖拽滑动在场景模型表面
        this.onModellibLeftClick();
        break;
      case ClickMode.Way: // 道路
        // 处理道路工具的鼠标抬起事件
        if (this.roadTool) {
          this.roadTool.onPointerUp(event);
          // 阻止默认的右键菜单
          if (event.button === 2) {
            event.preventDefault();
            event.stopPropagation();
          }
        }
        break;
      case ClickMode.CreateObject: // 放置基础模型
        this.onCreateObjectLeftClick();
        break;
      default:
        break;
    }
  }

  onCreateObjectLeftClick() {
    if (this.currentOperateModel) {
      this.create3DObject.createObject(
        this.currentOperateModel._ObjectType,
        this.currentOperateModel.position.clone(),
        [0, 0, 0, 1],
        new Vector3(1, 1, 1),
        null,
        null,
        (obj) => {
          // 历史记录相关
          {
            const state = {
              obj,
            };
            const cmd = new CommonCommand(
              state,
              (arg) => {
                this.create3DObject.deleteByModelId(arg.obj.modelId);
                this.manager.materialManager.deleteNode(arg.obj);
                const index = this.manager.sceneManager.singleModels.indexOf(
                  arg.obj
                );
                if (index >= 0)
                  this.manager.sceneManager.singleModels.splice(index, 1);
                // 前端更新结构树
                if (!this.manager.isInVR()) {
                  store.dispatch(setTreeList(this.getMeshTree()));
                  // 更新前端场景模型面板
                  this._updateToWebUI();
                }
                // VR端更新结构树
                else if (this.manager.isInVR()) {
                  this.manager.vrManager.updateHierarchy(
                    [arg.obj.nodeId],
                    "add"
                  );
                  this.manager.vrManager.selectHiarerchyNodes([arg.obj.nodeId]);
                  this.manager.vrManager.setModelComponent(arg.obj);
                }
              },
              (arg) => {
                this.create3DObject.objectArray.push(arg.obj);
                this.manager.materialManager.addNewModel(arg.obj);

                this.manager.sceneManager.scene.add(arg.obj);
                this.manager.sceneManager.singleModels.push(arg.obj);

                // 前端更新结构树
                if (!this.manager.isInVR()) {
                  store.dispatch(setTreeList(this.getMeshTree()));
                  // 更新前端场景模型面板
                  this._updateToWebUI();
                }
                // VR端更新结构树
                else if (this.manager.isInVR()) {
                  this.manager.vrManager.updateHierarchy(
                    [arg.obj.nodeId],
                    "add"
                  );
                  this.manager.vrManager.selectHiarerchyNodes([arg.obj.nodeId]);
                  this.manager.vrManager.setModelComponent(arg.obj);
                }
              }
            );
            this.manager.addHistory(cmd);
          }

          setTimeout(() => {
            this.manager.materialManager.addNewModel(obj);

            this.manager.sceneManager.scene.add(obj);
            this.manager.sceneManager.singleModels.push(obj);

            // 前端更新结构树
            if (!this.manager.isInVR()) {
              store.dispatch(setTreeList(this.getMeshTree()));
              // 更新前端场景模型面板
              this._updateToWebUI();
            }

            // VR端更新结构树
            else if (this.manager.isInVR()) {
              this.manager.vrManager.updateHierarchy([obj.nodeId], "add");
              this.manager.vrManager.selectHiarerchyNodes([obj.nodeId]);
              this.manager.vrManager.setModelComponent(obj);
            }

            if (this.manager.isInVR()) {
              this.manager.vrManager.makeSystemTip("放置成功");
            } else message.info("放置成功");
          }, 100);
        },
        true
      );
    }
  }

  /**
   * 模型库面板左键点击场景模型时的回调
   * @param {*} point
   */
  onModellibLeftClick(point) {
    // 点击到mesh
    if (point) {
      // 按阵列放置选中的模型库模型
      if (this.modelLibArgs.modelLibType === "matrix") {
        if (this.currentOperateModel) {
          const copyObjs = this._copyObj(
            this.currentOperateModel,
            this.modelLibArgs.row,
            this.modelLibArgs.col,
            this.modelLibArgs.dis
          );
          // y轴旋转随机 小范围缩放随机 (幅度 0.1)
          if (this.modelLibArgs.random) {
            for (var i = 0; i < copyObjs.length; i++) {
              const angle = Math.random() * Math.PI * 2;
              copyObjs[i].rotation.y = angle;
              const scale = 1 + Math.random() * this.modelRandomScaleOffset;
              copyObjs[i].scale.multiplyScalar(scale);
              copyObjs[i].updateMatrixWorld();

              // 靠地居中
              const box = new Box3().expandByObject(copyObjs[i]);
              let center = box.getCenter(new Vector3());
              const size = box.getSize(new Vector3());
              center.y -= size.y / 2;
              const offset = copyObjs[i].position.clone().sub(center);
              copyObjs[i].position.copy(point.clone().add(offset));
            }
          }
          if (this.skyUtil) {
            this.skyUtil.updateShadowMapSize(
              this.manager.sceneManager.singleModels
            );
          }
          if (this.manager.isInVR()) {
            this.manager.vrManager.makeSystemTip("放置成功");
          } else message.info("放置成功");

          // 历史记录
          {
            const state = {
              objs: [].concat(copyObjs),
            };
            const cmd = new CommonCommand(
              state,
              (arg) => {
                const ids = [];
                for (var i = 0; i < arg.objs.length; i++) {
                  ids.push(arg.objs[i].nodeId);
                  this.copyUtil.deleteByModelId(arg.objs[i].modelId);
                  this.manager.materialManager.deleteNode(arg.objs[i]);
                  let index = this.manager.sceneManager.singleModels.indexOf(
                    arg.objs[i]
                  );
                  if (index >= 0)
                    this.manager.sceneManager.singleModels.splice(index, 1);
                  if (this.manager.isInVR())
                    this.manager.vrManager.removeModelComponent(obj);
                }

                // 前端更新结构树
                if (!this.manager.isInVR())
                  store.dispatch(setTreeList(this.getMeshTree()));
                // VR端更新结构树
                else if (this.manager.isInVR()) {
                  this.manager.vrManager.updateHierarchy(ids, "delete");
                }
              },
              (arg) => {
                const ids = [];
                for (var i = 0; i < arg.objs.length; i++) {
                  ids.push(arg.objs[i].nodeId);

                  this.copyUtil.copyRecord.push(arg.objs[i]._record);
                  arg.objs[i]._record.model._copySrcObj._copys.push(
                    arg.objs[i]._record.model
                  );

                  this.manager.materialManager.restoreNode(arg.objs[i]);
                  this.manager.sceneManager.singleModels.push(arg.objs[i]);
                }

                // 前端更新结构树
                if (!this.manager.isInVR())
                  store.dispatch(setTreeList(this.getMeshTree()));
                // VR端更新结构树
                else if (this.manager.isInVR()) {
                  this.manager.vrManager.updateHierarchy(ids, "add");
                }
              }
            );
            this.manager.addHistory(cmd);
          }
        }
      }
      // 按曲线放置选中的模型库模型
      else if (this.modelLibArgs.modelLibType === "curve") {
        this.curveUtil.addPoint(point);
      }
    }
    // 点击到空白处
    else {
      // 按阵列放置选中的模型库模型
      if (
        this.modelLibArgs.modelLibType === "matrix" &&
        this.currentOperateModel
      ) {
        const copyObjs = this._copyObj(this.currentOperateModel);
        // y轴旋转随机 小范围缩放随机 (幅度 0.1)
        if (this.modelLibArgs.random) {
          const point = Util.screenToWorld(
            event,
            this.domElement,
            this.manager.sceneManager.activeCamera,
            this.dis2Camera
          );

          for (var i = 0; i < copyObjs.length; i++) {
            const angle = Math.random() * Math.PI * 2;
            copyObjs[i].rotation.y = angle;
            const scale = 1 + Math.random() * this.modelRandomScaleOffset;
            copyObjs[i].scale.multiplyScalar(scale);
            copyObjs[i].updateMatrixWorld();

            // 靠地居中
            const box = new Box3().expandByObject(copyObjs[i]);
            let center = box.getCenter(new Vector3());
            const size = box.getSize(new Vector3());
            center.y -= size.y / 2;
            const offset = copyObjs[i].position.clone().sub(center);
            copyObjs[i].position.copy(point.clone().add(offset));
          }
        }

        if (this.skyUtil) {
          this.skyUtil.updateShadowMapSize(
            this.manager.sceneManager.singleModels
          );
        }

        if (this.manager.isInVR()) {
          this.manager.vrManager.makeSystemTip("放置成功");
        } else message.info("放置成功");

        // 历史记录
        {
          const state = {
            objs: [].concat(copyObjs),
          };
          const cmd = new CommonCommand(
            state,
            (arg) => {
              const ids = [];
              for (var i = 0; i < arg.objs.length; i++) {
                ids.push(arg.objs[i].nodeId);
                this.copyUtil.deleteByModelId(arg.objs[i].modelId);
                this.manager.materialManager.deleteNode(arg.objs[i]);
                let index = this.manager.sceneManager.singleModels.indexOf(
                  arg.objs[i]
                );
                if (index >= 0)
                  this.manager.sceneManager.singleModels.splice(index, 1);
                if (this.manager.isInVR())
                  this.manager.vrManager.removeModelComponent(obj);
              }

              // 前端更新结构树
              if (!this.manager.isInVR())
                store.dispatch(setTreeList(this.getMeshTree()));
              // VR端更新结构树
              else if (this.manager.isInVR()) {
                this.manager.vrManager.updateHierarchy(ids, "delete");
              }
            },
            (arg) => {
              const ids = [];
              for (var i = 0; i < arg.objs.length; i++) {
                ids.push(arg.objs[i].nodeId);

                this.copyUtil.copyRecord.push(arg.objs[i]._record);
                arg.objs[i]._record.model._copySrcObj._copys.push(
                  arg.objs[i]._record.model
                );

                this.manager.materialManager.restoreNode(arg.objs[i]);
                this.manager.sceneManager.singleModels.push(arg.objs[i]);
              }

              // 前端更新结构树
              if (!this.manager.isInVR())
                store.dispatch(setTreeList(this.getMeshTree()));
              // VR端更新结构树
              else if (this.manager.isInVR()) {
                this.manager.vrManager.updateHierarchy(ids, "add");
              }
            }
          );
          this.manager.addHistory(cmd);
        }
      } else {
        //this.clickMeshToHierarchy(null, false);
        // this.pointNull();
      }
    }
  }

  /**
   * 材质库模式选中材质
   * @param {*} mat
   * @param {*} needHistory
   */
  onSelectMatForMatlib(mat = null, needHistory = true) {
    // 历史记录
    if (needHistory && (this.manager.materialManager.currentSelectMat || mat)) {
      const state = {
        oldMat: this.manager.materialManager.currentSelectMat,
        newMat: mat,
      };
      const cmd = new CommonCommand(
        state,
        (arg) => {
          this.onSelectMatForMatlib(arg.oldMat, false);
        },
        (arg) => {
          this.onSelectMatForMatlib(arg.newMat, false);
        }
      );
      this.manager.addHistory(cmd);
    }

    this.manager.materialManager.setSelectMat(mat);
    // 更新前端材质库面板
    this._updateWebMatlib();
  }

  /**
   * 框选中物体后的回调
   * @param {*} objs
   */
  onBoxSelectCB(objs) {
    if (!objs || objs.length <= 0) {
      this.clickMeshToHierarchy(null);
      this.pointNull();
      return;
    }
    let ids = [];
    for (var i = 0; i < objs.length; i++) {
      if (objs[i]._root) {
        objs[i] = objs[i]._root;
        ids.push(objs[i]._root.nodeId);
      }
    }
    this.clickMeshToHierarchy(ids);
  }

  updateProgress = (value) => {
    // console.log(value + "%");
    if (this.manager.isInVR()) {
      this.manager.vrManager.setProgress(value);
    } else {
      if (this.updateProgressCbFn) this.updateProgressCbFn(value);
    }

    // 更新HTML5进度条
    if (this.progressBar) {
      this.progressBar.value = value;
    }
    
    // 更新百分比文本
    if (this.percentText) {
      this.percentText.textContent = Math.round(value) + '%';
    }

    // 当加载进度达到100%时隐藏蒙版
    if (value >= 100 && this.loadingOverlay) {
      setTimeout(() => {
        if (this.loadingOverlay && this.loadingOverlay.parentNode) {
          this.loadingOverlay.parentNode.removeChild(this.loadingOverlay);
          this.loadingOverlay = null;
          this.progressBar = null;
          this.percentText = null;
        }
      }, 300); // 稍微延迟一下，让用户看到100%的状态
    }
  };

  // 设置更新进度信息的方法
  setUpdateProgressCb = (cb) => {
    this.updateProgressCbFn = cb;
  };

  setLoadModellibProgressCB(cb) {
    this.loadModellibProgressCB = cb;
  }

  //获取灯光数据  --TKB
  getLightData() {
    var lightData = {};
    lightData.sunAngle = this.getSunAngle();
    lightData.ambientIntensity = this.getAmbientIntensity();
    lightData.ambientColor = this.getAmbientColor();
    lightData.fogColor = this.getFogColor();
    lightData.fogDensity = this.getFogDensity();

    return lightData;
  }

  //材质编辑器模块,返回当前正在操作的材质的截图Uri，base64
  getCurrentSnapshotUrl() {
    return this.manager.materialManager.getCurrentSnapshotUrl();
  }

  //材质编辑器模块,当在场景中没有点击到物体时，取消物体的高亮显示   --TKB
  pointNull() {
    this.manager.materialManager.setSelectMat();
  }

  //材质编辑器模块,获取mesh树结构   --TKB
  getMeshTree() {
    return this.manager.materialManager.getMeshTree();
  }

  // 保存接口
  save = async (saveInfoCB) => {
    this.manager.sceneManager.detach();

    let params = {};

    params.assetid = this.options.assetid;
    params.modelInfos = [];

    params.lightInfos = this.getLightData();

    params.vr = this.manager.vrSettingManager.getVrData();

    if (this.create3DObject) params.object3Ds = this.create3DObject.save();

    if (this.copyUtil) params.copyObjs = this.copyUtil.save();

    // 材质数据
    const matData = this.manager.materialManager.save();
    params.materialInfos = matData;

    const peperSingles = [];

    function getModelInfo(curModel) {
      curModel.updateMatrixWorld();
      const curModelInfo = {};
      let modelId = curModel.modelId;
      curModelInfo.id = modelId;
      curModelInfo.name = curModel.name;
      if (curModel.userData && curModel.userData.modelFrom) {
        curModelInfo.modelFrom = curModel.userData.modelFrom;
      }
      let state = {
        visible: curModel._delete
          ? "delete"
          : curModel.visible
          ? "true"
          : "false",
        position: curModel.position.clone(),
        scale: curModel.scale.clone(),
        rotation: curModel.rotation.clone(),
      };
      curModelInfo.state = state;
      return curModelInfo;
    }

    console.log(this.manager.sceneManager.singleModels);
    for (var i = 0; i < this.manager.sceneManager.singleModels.length; i++) {
      const curModel = this.manager.sceneManager.singleModels[i];
      if (curModel._ObjectType !== undefined || curModel.isCopyObj) continue; // 跳过基本几何体和复制体

      // 图纸模型的单体
      if (curModel._isPeperSingle) {
        peperSingles.push(curModel);
        continue;
      }

      const curModelInfo = getModelInfo(curModel);
      curModelInfo.modelurl = curModel._modelUrl;
      params.modelInfos.push(curModelInfo);
    }
    // 图纸模型的单体
    const peperInfos = {
      updateTime: this.options.modelInfo[0].updatedAt,
      singleStates: [],
    };
    for (var i = 0; i < peperSingles.length; i++) {
      const curModel = peperSingles[i];
      peperInfos.modelurl = curModel._modelUrl;
      const curModelInfo = getModelInfo(curModel);

      peperInfos.singleStates.push(curModelInfo);
    }
    params.modelInfos.push(peperInfos);

    let trueid = params.assetid;
    console.log("save", params);
    await axios
      .post(window.base + "/drawing/uploadConfig?id=" + trueid, params)
      .then((res) => {
        if (res.data.code === 200) {
          message.info("保存成功");

          // 是函数 用于VR中保存反馈
          if (
            Object.prototype.toString.call(saveInfoCB) === "[object Function]"
          ) {
            saveInfoCB("保存成功");
          }
        } else {
          message.info("保存失败");

          // 是函数 用于VR中保存反馈
          if (
            Object.prototype.toString.call(saveInfoCB) === "[object Function]"
          ) {
            saveInfoCB("保存失败");
          }
        }
      });
  };

  traverse(node, cb) {
    if (node) {
      cb(node);
    }
    if (node.children && node.children.length) {
      for (var i = 0; i < node.children.length; i++) {
        this.traverse(node.children[i], cb);
      }
    }
  }

  /**
   * 根据nodeId获取模型
   * @param {*} nodeId
   */
  getModelByNodeId(nodeId) {
    for (var i = 0; i < this.manager.sceneManager.singleModels.length; i++) {
      if (this.manager.sceneManager.singleModels[i].nodeId === nodeId) {
        return this.manager.sceneManager.singleModels[i];
      }
    }
    return null;
  }

  // --------------------------------------------------------测量工具函数
  /**
   * 修改测量类型  0：距离测量  1：角度测量  2：标高测量
   * @param {*} mode
   */
  setMeasureMode(mode) {
    if (this.measureUtil) this.measureUtil.setupMeasureMode(mode);
  }
  /**
   * 设置距离测量和标高测量的单位
   * @param {*} u  可选项为 "m","cm","mm"
   */
  setMeasureUnit(u) {
    if (this.measureUtil) this.measureUtil.setUnit(u);
  }

  /**
   * 根据id移除一条测量记录
   * @param {*} id
   */
  removeMeasureRecord(id) {
    if (this.measureUtil) this.measureUtil.removeRecordById(id);
  }

  /**
   * 移除所有测量记录
   */
  removeAllMeasureRecords() {
    if (this.measureUtil) this.measureUtil.removeAllRecords();
  }

  /**
   * 根据id设置测量记录是否可见
   * @param {*} id
   * @param {*} visible
   */
  setMeasureRecordVisibleById(id, visible) {
    if (this.measureUtil) this.measureUtil.setRecordVisibleById(id, visible);
  }

  /**
   * 设置所有测量记录是否可见
   * @param {*} visible
   */
  setAllMeasireRecordVisible(visible) {
    if (this.measureUtil) this.measureUtil.setAllRecordVisible(visible);
  }

  /**
   * 获取所有测量记录
   * @returns
   */
  getAllMeasureRecord() {
    if (this.measureUtil) return this.measureUtil.getRecords();
  }

  /**
   * 设置通知前端更新测量数据的回调，通常在增加测量记录后被调用,回调函数带一个参数-所有的测量记录数组
   * @param {*} cb
   */
  setUpdateMeasureRecordCB(cb) {
    if (this.measureUtil) this.measureUtil.setUpdateMeasureRecordCB(cb);
  }

  // -----------------------------------------------------三视图
  /**
   * 切换透视图、顶视图、前视图、后视图、左视图、右视图
   * @param {*} view "default"/"top"/"front"/"back"/"left"/"right"
   */
  setupView(view) {
    // 历史记录相关
    {
      const state = {
        oldState: this.manager.sceneManager.getView(),
        newState: view,
      };
      const cmd = new CommonCommand(
        state,
        () => {
          this.manager.sceneManager.setupView(state.oldState);
          if (this.boxSelection)
            this.boxSelection.setCamera(this.manager.sceneManager.activeCamera);

          //  通知前端更新
          store.dispatch(setActiveView(state.oldState));
        },
        () => {
          this.manager.sceneManager.setupView(state.newState);
          if (this.boxSelection)
            this.boxSelection.setCamera(this.manager.sceneManager.activeCamera);

          // 通知前端更新
          store.dispatch(setActiveView(state.newState));
        }
      );
      this.manager.addHistory(cmd);
    }

    this.manager.sceneManager.setupView(view);

    if (this.boxSelection)
      this.boxSelection.setCamera(this.manager.sceneManager.activeCamera);
  }

  //---------------------------------------------------天空面板
  /**
   * 获取太阳角度
   * @returns 0-1的小数
   */
  getSunAngle() {
    return this.skyUtil.getSunAngle();
  }

  /**
   * 设置太阳角度
   * @param {Number} value 0-1的数值
   */
  setSunAngle(v) {
    // 历史记录相关
    {
      if (this.sunAngleSrcValue === undefined) {
        this.sunAngleSrcValue = this.getSunAngle();
        //console.log(this.sunAngleSrcValue);
      }
      const state = {
        oldState: this.sunAngleSrcValue,
        newState: v,
      };
      let addUndoFun = () => {
        const cmd = new CommonCommand(
          state,
          () => {
            this.skyUtil.setSunAngle(state.oldState);
            //  通知前端更新
            this._updateSkyUI();
          },
          () => {
            this.skyUtil.setSunAngle(state.newState);
            //  通知前端更新
            this._updateSkyUI();
          }
        );
        this.sunAngleSrcValue = v;
        this.manager.addHistory(cmd);
      };

      // 避免拖拽滑动条时频繁添加历史记录，只在停止拖拽后500毫秒再添加历史记录
      if (this.setSunAngleTimer) {
        clearTimeout(this.setSunAngleTimer);
        this.setSunAngleTimer = null;
      }
      this.setSunAngleTimer = setTimeout(() => {
        addUndoFun();
        this.setSunAngleTimer = null;
      }, 500);
    }

    this.skyUtil.setSunAngle(v);
  }

  /**
   * 设置环境光强度
   * @param {*} v
   */
  setAmbientIntensity(v) {
    // 历史记录相关
    {
      if (this.ambientIntensitySrcValue === undefined) {
        this.ambientIntensitySrcValue = this.getAmbientIntensity();
      }

      const state = {
        oldState: this.ambientIntensitySrcValue,
        newState: v,
      };
      let addUndoFun = () => {
        const cmd = new CommonCommand(
          state,
          () => {
            this.manager.sceneManager.setAmbientIntensity(state.oldState);
            //  通知前端更新
            this._updateSkyUI();
          },
          () => {
            this.manager.sceneManager.setAmbientIntensity(state.newState);
            //  通知前端更新
            this._updateSkyUI();
          }
        );
        this.ambientIntensitySrcValue = v;
        this.manager.addHistory(cmd);
      };
      // 避免拖拽滑动条时频繁添加历史记录，只在停止拖拽后500毫秒再添加历史记录
      if (this.setAmbientIntensityTimer) {
        clearTimeout(this.setAmbientIntensityTimer);
      }
      this.setAmbientIntensityTimer = setTimeout(() => {
        addUndoFun();
        this.setAmbientIntensityTimer = null;
      }, 500);
    }

    this.manager.sceneManager.setAmbientIntensity(v);
  }

  /**
   * 获取环境光强度
   * @returns
   */
  getAmbientIntensity() {
    return this.manager.sceneManager.getAmbientIntensity();
  }

  /**
   * 设置环境光颜色
   * @param {*} c
   */
  setAmbientColor(c) {
    c = c.toLowerCase();

    // 历史记录相关
    const state = {
      oldState: this.getAmbientColor(),
      newState: c,
    };
    if (state.oldState === state.newState) return;
    const cmd = new CommonCommand(
      state,
      () => {
        this.manager.sceneManager.setAmbientColor((prev) => state.oldState);
        //  通知前端更新
        this._updateSkyUI();
      },
      () => {
        this.manager.sceneManager.setAmbientColor((prev) => state.newState);
        //  通知前端更新
        this._updateSkyUI();
      }
    );
    this.manager.addHistory(cmd);

    this.manager.sceneManager.setAmbientColor(c);
  }

  /**
   * 获取环境光颜色
   * @returns
   */
  getAmbientColor() {
    return this.manager.sceneManager.getAmbientColor();
  }

  /**
   * 设置雾的强度
   * @param {*} c 0-1的小数
   */
  setFogDensity(d) {
    // 将0-1的小数转换为0-0.05的数值
    d *= 0.05;

    // 历史记录相关
    {
      if (this.fogDensitySrcValue === undefined) {
        this.fogDensitySrcValue = this.manager.sceneManager.getFogDensity();
      }
      const state = {
        oldState: this.fogDensitySrcValue,
        newState: d,
      };
      let addUndoFun = () => {
        const cmd = new CommonCommand(
          state,
          () => {
            this.manager.sceneManager.setFogDensity(state.oldState);
            //  通知前端更新
            this._updateSkyUI();
          },
          () => {
            this.manager.sceneManager.setFogDensity(state.newState);
            //  通知前端更新
            this._updateSkyUI();
          }
        );
        this.fogDensitySrcValue = d;
        this.manager.addHistory(cmd);
      };
      // 避免拖拽滑动条时频繁添加历史记录，只在停止拖拽后500毫秒再添加历史记录
      if (this.setFogDensityTimer) {
        clearTimeout(this.setFogDensityTimer);
      }
      this.setFogDensityTimer = setTimeout(() => {
        addUndoFun();
        this.setFogDensityTimer = null;
      }, 500);
    }

    this.manager.sceneManager.setFogDensity(d);
    this.skyUtil.skyUniforms["fogEndHeight"].value = d / 0.025;
  }

  /**
   * 获取雾的强度
   * @returns 0-1的小数
   */
  getFogDensity() {
    // 获取的是0-0.05的数值
    let d = this.manager.sceneManager.getFogDensity();
    // 将0-0.05的数值转换为0-1的小数
    d /= 0.05;
    return d;
  }

  /**
   * 设置雾的颜色
   * @param {*} c
   */
  setFogColor(c) {
    c = c.toLowerCase();
    // 历史记录相关
    const state = {
      oldState: this.getFogColor(),
      newState: c,
    };
    if (state.oldState === state.newState) return;
    const cmd = new CommonCommand(
      state,
      () => {
        this.manager.sceneManager.setFogColor(state.oldState);
        const color = new Color(state.oldState);
        this.skyUtil.skyUniforms["fogColor"].value = new Vector3(
          color.r,
          color.g,
          color.b
        );
        //  通知前端更新
        this._updateSkyUI();
      },
      () => {
        this.manager.sceneManager.setFogColor(state.newState);
        const color = new Color(state.newState);
        this.skyUtil.skyUniforms["fogColor"].value = new Vector3(
          color.r,
          color.g,
          color.b
        );
        //  通知前端更新
        this._updateSkyUI();
      }
    );
    this.manager.addHistory(cmd);

    this.manager.sceneManager.setFogColor(c);
    const color = new Color(c);
    this.skyUtil.skyUniforms["fogColor"].value = new Vector3(
      color.r,
      color.g,
      color.b
    );
  }

  /**
   * 获取雾的颜色
   * @returns
   */
  getFogColor() {
    return this.manager.sceneManager.getFogColor();
  }

  /**
   * 更新天空面板
   */
  _updateSkyUI() {
    // 更新前端天空面板
    if (!this.manager.isInVR()) {
      const skyData = {
        sunAngle: this.getSunAngle(),
        weather: "sun",
        ambientIntensity: this.getAmbientIntensity(),
        ambientColor: this.getAmbientColor(),
        fogDensity: this.getFogDensity(),
        fogColor: this.getFogColor(),
      };
      //console.log(skyData);
      store.dispatch(setSkyInfo(skyData));
    }
  }

  /**
   * 设置点击交互模式
   * @param { ClickMode } mode 0:None 1:测量 2：场景模型 3：地形 4：道路 5：材质库 6：模型库 7：VR设置 地面编辑 8：VR设置 位置编辑
   */
  setClickMode(mode) {
    if (this.clickMode === mode) return;
    this.lastMode = this.clickMode;
    // console.error("setClickMode", mode);
    this.clickMode = mode;
    this.manager.vrManager.setClickMode(mode);
    this.manager.materialManager.setSelectMat();
    // 更新前端材质库面板
    this._updateWebMatlib();
    this.manager.sceneManager.detach();
    this._updateToWebUI();

    // 只有场景模型模式和模型库模式可以框选
    if (this.boxSelection) {
      if (
        this.clickMode === ClickMode.SceneModel ||
        this.clickMode === ClickMode.Model
      ) {
        this.boxSelection.changeMode(true);
      } else {
        this.boxSelection.changeMode(false);
      }
    }

    // 地形笔刷模式处理
    if (this.terrainBrush) {
      if (this.clickMode === ClickMode.Terrain) {
        this.terrainBrush.enable();
      } else {
        this.terrainBrush.disable();
      }
    }
    
    // 启用或禁用道路工具
    if (this.roadTool) {
      if (this.clickMode === ClickMode.Way) {
        this.roadTool.enable();
      } else {
        this.roadTool.disable();
      }
    }

    this.curveUtil.clear();
    this._clearCurveModel();

    //console.log(mode);
  }

  /**
   * 点击结构树时选中高亮对应的模型
   * @param {*} nodeId
   */
  clickHierarchy(nodeId) {
    const model = this.getModelByNodeId(nodeId);

    // 撤销相关
    {
      const currentSelectObj = this.manager.sceneManager.currentSelectObj;
      const oldIds = [];
      for (var i = 0; i < currentSelectObj.length; i++) {
        oldIds.push(currentSelectObj[i].nodeId);
      }

      const state = {
        oldObj: currentSelectObj,
        oldIds: oldIds,
        newObj: [model],
        newIds: [nodeId],
      };
      const cmd = new CommonCommand(
        state,
        (arg) => {
          if (arg.oldObj && arg.oldObj.length > 0) {
            this.clickMeshToHierarchy(arg.oldIds, false);
          } else {
            this.clickMeshToHierarchy(null, false);
            this.pointNull();
          }
        },
        (arg) => {
          if (arg.newObj && arg.newObj.length > 0) {
            this.clickMeshToHierarchy(arg.newIds, false);
          } else {
            this.clickMeshToHierarchy(null, false);
            this.pointNull();
          }
        }
      );
      this.manager.historyManager.push(cmd);
    }

    if (model) this.manager.sceneManager.attach(model);
    if (this.clickMode === ClickMode.SceneModel) {
      this._updateToWebUI();
    }

    // 通知VR端选中模型
    if (this.manager.isInVR()) {
      this.manager.vrManager.setCurrentSelectModel();
    } else {
      store.dispatch(setActiveTreeNodeId([nodeId]));
    }
  }

  /**
   * 点选或框选中模型时高亮结构树上对应的节点
   * @param {*} ids 模型nodeId的数组
   */
  clickMeshToHierarchy(ids, needHistory = true) {
    if (!ids) ids = [];
    if (!Array.isArray(ids)) ids = [ids];

    const newObjs = [];
    for (var i = 0; i < ids.length; i++) {
      newObjs.push(this.getModelByNodeId(ids[i]));
    }

    // 撤销相关
    const currentSelectObj = this.manager.sceneManager.currentSelectObj;
    if (needHistory && (currentSelectObj.length > 0 || ids.length > 0)) {
      const oldIds = [];
      for (var i = 0; i < currentSelectObj.length; i++) {
        oldIds.push(currentSelectObj[i].nodeId);
      }

      const state = {
        oldObj: currentSelectObj,
        oldIds: oldIds,
        newObj: newObjs,
        newIds: ids,
      };
      const cmd = new CommonCommand(
        state,
        (arg) => {
          if (arg.oldObj && arg.oldObj.length > 0) {
            this.clickMeshToHierarchy(arg.oldIds, false);
          } else {
            this.clickMeshToHierarchy(null, false);
            this.pointNull();
          }
        },
        (arg) => {
          if (arg.newObj && arg.newObj.length > 0) {
            this.clickMeshToHierarchy(arg.newIds, false);
          } else {
            this.clickMeshToHierarchy(null, false);
            this.pointNull();
          }
        }
      );
      this.manager.historyManager.push(cmd);
    }

    this.manager.sceneManager.attach(newObjs);

    if (!this.manager.isInVR()) {
      this._updateToWebUI();
      store.dispatch(setActiveTreeNodeId(ids));
    } else {
      this.manager.vrManager.selectHiarerchyNodes(ids);
    }
  }

  // ----------------------------------------- 场景模型面板
  /**
   * 设置当前选中模型的位置
   * @param {*} pos
   */
  setModelPosition(pos) {
    const target = this.manager.sceneManager.currentSelectParent;
    if (target) {
      const oldState = {
        position: target.position.clone(),
        scale: target.scale.clone(),
        quaternion: target.quaternion.clone(),
      };
      target.position.set(pos.x * 1, pos.y * 1, pos.z * 1);

      const cmd = new TransformCommand(
        target,
        oldState,
        this.updateUIByChangeModel.bind(this)
      );
      this.manager.addHistory(cmd);
    }
  }

  /**
   * 设置当前选中模型的缩放
   * @param {*} scale
   */
  setModelScale(scale) {
    const target = this.manager.sceneManager.currentSelectParent;
    if (target) {
      const oldState = {
        position: target.position.clone(),
        scale: target.scale.clone(),
        quaternion: target.quaternion.clone(),
      };

      target.scale.set(scale.x * 1, scale.y * 1, scale.z * 1);

      const cmd = new TransformCommand(
        target,
        oldState,
        this.updateUIByChangeModel.bind(this)
      );
      this.manager.addHistory(cmd);
    }
  }

  /**
   * 设置当前选中模型的旋转
   * @param {*} rot
   * @returns
   */
  setModelRotation(rot) {
    const target = this.manager.sceneManager.currentSelectParent;
    if (!target) return;
    const oldState = {
      position: target.position.clone(),
      scale: target.scale.clone(),
      quaternion: target.quaternion.clone(),
    };

    let euler = new Euler(
      Util.degToRad(parseFloat(rot.x * 1)),
      Util.degToRad(parseFloat(rot.y * 1)),
      Util.degToRad(parseFloat(rot.z * 1))
    );
    target.rotation.copy(euler);

    const cmd = new TransformCommand(
      target,
      oldState,
      this.updateUIByChangeModel.bind(this)
    );
    this.manager.addHistory(cmd);
  }

  /**
   * 阵列复制当前选中的对象
   * @param {*} row
   * @param {*} col
   * @param {*} dis
   */
  copyObj(row = 1, col = 1, dis = 1) {
    const selectObjs = this.manager.sceneManager.currentSelectObj;

    if (selectObjs.length > 0 && this.copyUtil) {
      this.manager.sceneManager.detach();
      const copyObjs = this._copyObj(selectObjs, row, col, dis);

      this.manager.sceneManager.attach(copyObjs);

      this._updateToWebUI();

      if (!this.manager.isInVR()) {
        message.info("复制成功");
      } else {
        this.manager.vrManager.makeSystemTip("复制成功");
      }

      // 历史记录相关
      {
        const state = {
          objs: [].concat(copyObjs),
          srcSelectObjs: [].concat(selectObjs),
        };
        const cmd = new CommonCommand(
          state,
          (state) => {
            this.manager.sceneManager.detach();
            let ids = [];
            for (var i = 0; i < state.objs.length; i++) {
              ids.push(state.objs[i].nodeId);
              this.copyUtil.deleteByModelId(state.objs[i].modelId);
              this.manager.materialManager.deleteNode(state.objs[i]);
              let index = this.manager.sceneManager.singleModels.indexOf(
                state.objs[i]
              );
              if (index >= 0)
                this.manager.sceneManager.singleModels.splice(index, 1);
              if (this.manager.isInVR())
                this.manager.vrManager.removeModelComponent(obj);
            }
            this.manager.sceneManager.attach(state.srcSelectObjs);
            // 前端更新结构树
            if (!this.manager.isInVR())
              store.dispatch(setTreeList(this.getMeshTree()));
            // VR端更新结构树
            else if (this.manager.isInVR()) {
              this.manager.vrManager.updateHierarchy(ids, "delete");
              ids = [];
              for (var i = 0; i < state.srcSelectObjs.length; i++) {
                ids.push(state.srcSelectObjs[i].nodeId);
              }
              this.manager.vrManager.selectHiarerchyNodes(ids);
              this.manager.vrManager.setCurrentSelectModel();
            }
          },
          (state) => {
            this.manager.sceneManager.detach();
            const ids = [];
            for (var i = 0; i < state.objs.length; i++) {
              ids.push(state.objs[i].nodeId);

              this.copyUtil.copyRecord.push(state.objs[i]._record);
              state.objs[i]._record.model._copySrcObj._copys.push(
                state.objs[i]._record.model
              );

              this.manager.materialManager.restoreNode(state.objs[i]);
              this.manager.sceneManager.singleModels.push(state.objs[i]);
            }
            this.manager.sceneManager.attach(state.objs);
            // 前端更新结构树
            if (!this.manager.isInVR())
              store.dispatch(setTreeList(this.getMeshTree()));
            // VR端更新结构树
            else if (this.manager.isInVR()) {
              this.manager.vrManager.updateHierarchy(ids, "add");
              this.manager.vrManager.selectHiarerchyNodes(ids);
              this.manager.vrManager.setCurrentSelectModel();
            }
          }
        );
        this.manager.addHistory(cmd);
      }
    }
  }

  _copyObj(srcObj, row = 1, col = 1, dis = 1) {
    let copyObjs = [];
    const ids = [];
    this.copyUtil.copy(srcObj, row, col, dis, (obj) => {
      this.manager.materialManager.addNewModel(obj);

      this.manager.sceneManager.scene.add(obj);
      this.manager.sceneManager.singleModels.push(obj);
      copyObjs.push(obj);

      ids.push(obj.nodeId);
      if (this.manager.isInVR()) this.manager.vrManager.setModelComponent(obj);
    });

    // 前端更新结构树
    if (!this.manager.isInVR()) store.dispatch(setTreeList(this.getMeshTree()));
    // VR端更新结构树
    else if (this.manager.isInVR()) {
      this.manager.vrManager.updateHierarchy(ids, "add");
      this.manager.vrManager.selectHiarerchyNodes(ids);
      this.manager.vrManager.setCurrentSelectModel();
    }
    return copyObjs;
  }

  /**
   * 删除当前选中的模型
   */
  deleteCurrentModel(fromHistory = false) {
    const selectObjs = [].concat(this.manager.sceneManager.currentSelectObj);
    if (selectObjs.length === 0) return;

    // 历史记录相关
    if (!fromHistory) {
      const state = {
        objs: [].concat(selectObjs),
      };
      const cmd = new CommonCommand(
        state,
        (state) => {
          const ids = [];
          for (var i = 0; i < state.objs.length; i++) {
            ids.push(state.objs[i].nodeId);
            if (this.manager.isInVR()) {
              this.manager.vrManager.removeModelComponent(state.objs[i]);
            }
            // 基本几何体
            if (state.objs[i]._ObjectType !== undefined) {
              this.create3DObject.objectArray.push(state.objs[i]);
            }
            // 复制体
            else if (state.objs[i].isCopyObj) {
              this.copyUtil.copyRecord.push(state.objs[i]._record);
              state.objs[i]._record.model._copySrcObj._copys.push(
                state.objs[i]._record.model
              );
            }
            this.manager.materialManager.restoreNode(state.objs[i]);
            if (!state.objs[i]._isPeperSingle)
              this.manager.sceneManager.singleModels.push(state.objs[i]);
          }
          this.manager.sceneManager.attach(state.objs);
          // 前端更新结构树
          if (!this.manager.isInVR())
            store.dispatch(setTreeList(this.getMeshTree()));
          // VR端更新结构树
          else if (this.manager.isInVR()) {
            this.manager.vrManager.updateHierarchy(ids, "delete");
          }

          // 更新前端场景模型面板
          this._updateToWebUI();
        },
        () => {
          this.deleteCurrentModel(true);
        }
      );
      this.manager.addHistory(cmd);
      //console.log("添加删除的历史记录", cmd);
    }

    this.manager.sceneManager.detach();
    const ids = [];
    if (selectObjs.length > 0) {
      for (var i = 0; i < selectObjs.length; i++) {
        if (this.manager.isInVR()) {
          this.manager.vrManager.removeModelComponent(selectObjs[i]);
        }

        // 清除基本几何体
        if (selectObjs[i]._ObjectType !== undefined) {
          this.create3DObject.deleteByModelId(selectObjs[i].modelId);
        }
        // 清除复制体
        else if (selectObjs[i].isCopyObj) {
          this.copyUtil.deleteByModelId(selectObjs[i].modelId);
        }
        ids.push(selectObjs[i].nodeId);

        this.manager.materialManager.deleteNode(selectObjs[i]);

        function removeFromArray(array) {
          let index = array.indexOf(selectObjs[i]);
          if (index > -1) {
            array.splice(index, 1);
          }
        }

        if (!selectObjs[i]._isPeperSingle)
          removeFromArray(this.manager.sceneManager.singleModels);
      }
      // 前端更新结构树
      if (!this.manager.isInVR())
        store.dispatch(setTreeList(this.getMeshTree()));
      // VR端更新结构树
      else if (this.manager.isInVR()) {
        this.manager.vrManager.updateHierarchy(ids, "delete");
      }

      // 更新前端场景模型面板
      this._updateToWebUI();

      if (!fromHistory) {
        if (this.manager.isInVR()) {
          this.manager.vrManager.makeSystemTip("擦除成功editor1");
        } else message.info("擦除成功editor2");
      }
    }
  }

  /**
   * 场景模型面板中将当前选中模型的信息发送给前端，如果没有选中模型则为null
   */
  _updateToWebUI() {
    if (this.manager.isInVR()) return;
    // 通知前端高亮对应节点
    const obj = this.manager.sceneManager.currentSelectParent;
    if (!obj) {
      store.dispatch(
        setSceneModelInfo({
          name: "",
          position: {
            x: 0,
            y: 0,
            z: 0,
          },
          scale: {
            x: 1,
            y: 1,
            z: 1,
          },
          rotation: {
            x: 0,
            y: 0,
            z: 0,
          },
          isSelect: false,
        })
      );
    } else {
      let tempRot = new Euler().setFromQuaternion(obj.quaternion.clone());

      const res = {
        name: obj.children.length > 1 ? "多个模型" : obj.name,
        position: {
          x: obj.position.x.toFixed(4) * 1,
          y: obj.position.y.toFixed(4) * 1,
          z: obj.position.z.toFixed(4) * 1,
        },
        scale: {
          x: obj.scale.x.toFixed(4) * 1,
          y: obj.scale.y.toFixed(4) * 1,
          z: obj.scale.z.toFixed(4) * 1,
        },
        rotation: {
          x: Util.radToDeg(tempRot.x).toFixed(4) * 1,
          y: Util.radToDeg(tempRot.y).toFixed(4) * 1,
          z: Util.radToDeg(tempRot.z).toFixed(4) * 1,
        },
        isSelect: true,
      };

      store.dispatch(setSceneModelInfo(res));
    }
  }

  /**
   * 模型修改后更新数据到前端
   */
  updateUIByChangeModel() {
    this._updateToWebUI();
  }

  setCopyArgs(row, col, dis) {
    row *= 1;
    col *= 1;
    dis *= 1;

    const oldState = {};
    const newState = {};

    let hasChange = false;
    if (row !== this.copyArgs.row) {
      oldState.row = this.copyArgs.row;
      newState.row = row;
      this.copyArgs.row = row;
      hasChange = true;
    }
    if (col !== this.copyArgs.col) {
      oldState.col = this.copyArgs.col;
      newState.col = col;
      this.copyArgs.col = col;
      hasChange = true;
    }
    if (dis !== this.copyArgs.dis) {
      oldState.dis = this.copyArgs.dis;
      newState.dis = dis;
      this.copyArgs.dis = dis;
      hasChange = true;
    }

    if (hasChange) {
      const state = {
        oldState,
        newState,
      };
      const cmd = new CommonCommand(
        state,
        (state) => {
          if (state.oldState.row !== undefined)
            this.copyArgs.row = state.oldState.row;
          if (state.oldState.col !== undefined)
            this.copyArgs.col = state.oldState.col;
          if (state.oldState.dis !== undefined)
            this.copyArgs.dis = state.oldState.dis;
          store.dispatch(
            setSceneModelSetting({
              row: this.copyArgs.row,
              column: this.copyArgs.col,
              gap: this.copyArgs.dis,
            })
          );
        },
        (state) => {
          if (state.newState.row !== undefined)
            this.copyArgs.row = state.newState.row;
          if (state.newState.col !== undefined)
            this.copyArgs.col = state.newState.col;
          if (state.newState.dis !== undefined)
            this.copyArgs.dis = state.newState.dis;
          store.dispatch(
            setSceneModelSetting({
              row: this.copyArgs.row,
              column: this.copyArgs.col,
              gap: this.copyArgs.dis,
            })
          );
        }
      );
      this.manager.addHistory(cmd);
    }
  }

  // ----------------------------------------- 材质库面板
  /**
   * 设置当前选中材质颜色
   * @param {*} color
   */
  setColor(color) {
    // 历史记录相关
    {
      const state = {
        oldColor: this.manager.materialManager.getColor(),
        newColor: color,
      };
      const cmd = new CommonCommand(
        state,
        (state) => {
          this.manager.materialManager.setColor((prev) => state.oldColor);
          this._updateWebMatlib();
        },
        (state) => {
          this.manager.materialManager.setColor((prev) => state.newColor);
          this._updateWebMatlib();
        }
      );
      this.manager.addHistory(cmd);
    }

    this.manager.materialManager.setColor(color);

    // 修改颜色后更新前端材质库面板
    this._updateWebMatlib();
  }

  /**
   * 获取当前选中材质颜色
   * @returns
   */
  getColor() {
    return this.manager.materialManager.getColor();
  }

  /**
   * 设置当前选中材质的纹理缩放
   * @param {*} scale
   */
  setTextureScale(scale, needHistory = true) {
    // 历史记录相关
    if (needHistory) {
      if (this.textureScaleSrcValue === undefined) {
        this.textureScaleSrcValue = this.getTextureScale();
      }
      const state = {
        oldState: this.textureScaleSrcValue,
        newState: scale,
      };
      let addUndoFun = () => {
        const cmd = new CommonCommand(
          state,
          () => {
            this.setTextureScale(state.oldState, false);
            //  通知前端更新
            this._updateWebMatlib();
          },
          () => {
            this.setTextureScale(state.newState, false);
            //  通知前端更新
            this._updateWebMatlib();
          }
        );
        this.textureScaleSrcValue = scale;
        this.manager.addHistory(cmd);
      };
      // 避免拖拽滑动条时频繁添加历史记录，只在停止拖拽后500毫秒再添加历史记录
      if (this.setTextureScaleTimer) {
        clearTimeout(this.setTextureScaleTimer);
      }
      this.setTextureScaleTimer = setTimeout(() => {
        addUndoFun();
        this.setTextureScaleTimer = null;
      }, 500);
    }

    this.manager.materialManager.setTextureScale(scale);
  }

  /**
   * 获取当前选中材质的纹理缩放
   * @returns
   */
  getTextureScale() {
    return this.manager.materialManager.getTextureScale();
  }

  /**
   *  设置当前选中材质的纹理方向
   */
  setTextureRotation(angle, fromHistory = true) {
    // 历史记录相关
    if (fromHistory) {
      if (this.textureRotationSrcValue === undefined) {
        this.textureRotationSrcValue = this.getTextureRotation();
      }
      const state = {
        oldState: this.textureRotationSrcValue,
        newState: angle,
      };
      let addUndoFun = () => {
        const cmd = new CommonCommand(
          state,
          () => {
            this.setTextureRotation(state.oldState, false);
            //  通知前端更新
            this._updateWebMatlib();
          },
          () => {
            this.setTextureRotation(state.newState, false);
            //  通知前端更新
            this._updateWebMatlib();
          }
        );
        this.textureRotationSrcValue = angle;
        this.manager.addHistory(cmd);
      };
      // 避免拖拽滑动条时频繁添加历史记录，只在停止拖拽后500毫秒再添加历史记录
      if (this.setTextureRotationTimer) {
        clearTimeout(this.setTextureRotationTimer);
      }
      this.setTextureRotationTimer = setTimeout(() => {
        addUndoFun();
        this.setTextureRotationTimer = null;
      }, 500);
    }

    this.manager.materialManager.setTextureRotation(angle);
  }

  /**
   * 获取当前选中材质的纹理方向 0-360
   * @returns
   */
  getTextureRotation() {
    return this.manager.materialManager.getTextureRotation();
  }

  /**
   * 点击材质库的材质替换当前选中材质
   * @param {*} url 材质路径
   */
  replaceMatFromLib(url, matName, cb = null) {
    //event.preventDefault();
    if (this.clickMode !== ClickMode.Material) {
      return;
    }

    this.manager.materialManager.replaceMatFromLib(
      url.indexOf("http://") >= 0 || url.indexOf("https://") >= 0
        ? url
        : window.base + "/" + url,
      matName,
      () => {
        // 更新前端材质库面板
        this._updateWebMatlib();

        this.textureRotationSrcValue = undefined;
        this.textureScaleSrcValue = undefined;

        cb && cb();
      }
    );
  }

  updateMatlibForHistory() {
    this.textureRotationSrcValue = undefined;
    this.mtextureScaleSrcValue = undefined;

    this._updateWebMatlib();
  }

  _updateWebMatlib() {
    if (this.manager.isInVR()) return;

    if (!this.manager.materialManager.currentSelectMat) {
      store.dispatch(
        setMaterialInfo({
          image: "/defaultMat.jpg",
          name: "",
          color: "#ffffff",
          scale: 1,
          direction: 0,
          isSelect: false,
        })
      );
      return;
    }

    const res = {
      image: this.getCurrentSnapshotUrl(),
      name: this.manager.materialManager.currentSelectMat.name,
      color: this.getColor(),
      scale: this.getTextureScale(),
      direction: this.getTextureRotation(),
      isSelect: true,
    };

    store.dispatch(setMaterialInfo(res));
  }

  /**
   * 从服务器加载材质库数据
   * @param {*} name 查询的材质名，为空则查询所有
   * @param {*} cb 查询结果回调函数，参数为查询结果数组
   */
  loadMaterialData(name = "", cb) {
    if (!name) name = "";
    // 发起GET请求
    fetch(window.base + "/material/search?name=" + name)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP错误 ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (cb) cb(data.data);
      })
      .catch((error) => console.error("请求失败:", error));
  }

  // ------------------------- 模型库面板

  setModellibAccuracy(accuracy) {
    if (this.modelLibArgs.accuracy !== accuracy) {
      // 历史记录相关
      {
        const state = {
          oldState: this.modelLibArgs.accuracy,
          newState: accuracy,
        };
        const cmd = new CommonCommand(
          state,
          (state) => {
            this.modelLibArgs.accuracy = state.oldState;
            store.dispatch(setModelAccuracy(state.oldState));
          },
          (state) => {
            this.modelLibArgs.accuracy = state.newState;
            store.dispatch(setModelAccuracy(state.newState));
          }
        );
        this.manager.addHistory(cmd);
      }

      this.modelLibArgs.accuracy = accuracy;
    }
  }

  /**
   * 设置模型库放置类型
   * @param {*} type "matrix"/"curve"
   */
  setModelLibType(type = "matrix", needHistory = true) {
    if (this.modelLibArgs.modelLibType === type) return;

    const oldState = this.modelLibArgs.modelLibType;

    if (type === "matrix") this.modelLibArgs.modelLibType = type;
    else this.modelLibArgs.modelLibType = "curve";

    this.curveUtil.clear();

    if (this.currentOperateModel) this.currentOperateModel.position.y = -10000;

    this._clearCurveModel();

    // 历史记录
    if (needHistory) {
      const state = {
        oldState: oldState,
        newState: this.modelLibArgs.modelLibType,
      };
      const cmd = new CommonCommand(
        state,
        (state) => {
          this.setModelLibType(state.oldState, false);
          store.dispatch(setModelArrange(state.oldState));
        },
        (state) => {
          this.setModelLibType(state.newState, false);
          store.dispatch(setModelArrange(state.newState));
        }
      );
      this.manager.addHistory(cmd);
    }
  }

  _updateModellibUI() {
    store.dispatch(
      setModelSetting({
        type: this.modelLibArgs.modelLibType,
        gap: this.modelLibArgs.dis,
        row: this.modelLibArgs.row,
        column: this.modelLibArgs.col,
        lineType: this.modelLibArgs.curveType,
        random: this.modelLibArgs.random,
      })
    );
  }

  /**
   * 设置矩阵放置参数：行、列、间距、是否随机 前端调用
   */
  setMatrixArgs(row, col, dis, isRandom) {
    row *= 1;
    col *= 1;
    dis *= 1;
    const oldState = {};
    const newState = {};

    let hasChange = false;
    if (this.modelLibArgs.row !== row) {
      oldState.row = this.modelLibArgs.row;
      newState.row = row;
      this.modelLibArgs.row = row;
      hasChange = true;
    }
    if (this.modelLibArgs.col !== col) {
      oldState.col = this.modelLibArgs.col;
      newState.col = col;
      this.modelLibArgs.col = col;
      hasChange = true;
    }
    if (this.modelLibArgs.dis !== dis) {
      oldState.dis = this.modelLibArgs.dis;
      newState.dis = dis;
      this.modelLibArgs.dis = dis;
      hasChange = true;
    }
    if (this.modelLibArgs.random !== isRandom) {
      oldState.random = this.modelLibArgs.random;
      newState.random = isRandom;
      this.modelLibArgs.random = isRandom;
      hasChange = true;
    }

    // 历史记录
    if (hasChange) {
      const state = {
        oldState: oldState,
        newState: newState,
      };
      const cmd = new CommonCommand(
        state,
        (state) => {
          if (state.oldState.row !== undefined)
            this.modelLibArgs.row = state.oldState.row;
          if (state.oldState.col !== undefined)
            this.modelLibArgs.col = state.oldState.col;
          if (state.oldState.dis !== undefined)
            this.modelLibArgs.dis = state.oldState.dis;
          if (state.oldState.random !== undefined)
            this.modelLibArgs.random = state.oldState.random;
          this._updateModellibUI();
        },
        (state) => {
          if (state.oldState.row !== undefined)
            this.modelLibArgs.row = state.newState.row;
          if (state.oldState.col !== undefined)
            this.modelLibArgs.col = state.newState.col;
          if (state.oldState.dis !== undefined)
            this.modelLibArgs.dis = state.newState.dis;
          if (state.oldState.random !== undefined)
            this.modelLibArgs.random = state.newState.random;
          this._updateModellibUI();
        }
      );
      this.manager.addHistory(cmd);
    }
  }

  /**
   * 设置曲线放置参数：间距、类型，是否随机 前端调用
   * @param {*} dis
   * @param {*} type "curve"/"line"
   * @param {*} isRandom
   */
  setCurveArgs(dis, type, isRandom) {
    dis *= 1;
    //console.log("setCurveArgs", dis, type, isRandom);

    const oldState = {};
    const newState = {};

    let hasChange = false;
    dis *= 1;
    if (dis <= 0) dis = 0.1;
    if (this.modelLibArgs.dis !== dis) {
      oldState.dis = this.modelLibArgs.dis;
      newState.dis = dis;
      this.modelLibArgs.dis = dis;
      hasChange = true;
    }
    if (type.value) type = type.value;
    if (this.modelLibArgs.curveType !== type) {
      oldState.type = this.modelLibArgs.curveType;
      newState.type = type;
      this.modelLibArgs.curveType = type;
      this.curveUtil.setType(type);
      hasChange = true;
    }
    if (this.modelLibArgs.random !== isRandom) {
      oldState.random = this.modelLibArgs.random;
      newState.random = isRandom;
      this.modelLibArgs.random = isRandom;
      hasChange = true;
    }

    if (hasChange) {
      this._generageModelByCurve();

      // 历史记录
      {
        const state = {
          oldState: oldState,
          newState: newState,
        };
        const cmd = new CommonCommand(
          state,
          (state) => {
            if (state.oldState.dis !== undefined)
              this.modelLibArgs.dis = state.oldState.dis;
            if (state.oldState.type !== undefined) {
              this.modelLibArgs.curveType = state.oldState.type;
              this.curveUtil.setType(this.modelLibArgs.curveType);

              store.dispatch(
                setModelType({
                  label:
                    this.modelLibArgs.curveType === "curve" ? "曲线" : "直线",
                  value: this.modelLibArgs.curveType,
                })
              );
            }
            if (state.oldState.random !== undefined)
              this.modelLibArgs.random = state.oldState.random;

            this._generageModelByCurve();
            this._updateModellibUI();
          },
          (state) => {
            if (state.newState.dis !== undefined)
              this.modelLibArgs.dis = state.newState.dis;
            if (state.newState.type !== undefined) {
              this.modelLibArgs.curveType = state.newState.type;
              this.curveUtil.setType(this.modelLibArgs.curveType);
              store.dispatch(
                setModelType({
                  label:
                    this.modelLibArgs.curveType === "curve" ? "曲线" : "直线",
                  value: this.modelLibArgs.curveType,
                })
              );
            }
            if (state.newState.random !== undefined)
              this.modelLibArgs.random = state.newState.random;

            this._generageModelByCurve();
            this._updateModellibUI();
          }
        );
        this.manager.addHistory(cmd);
      }
    }
  }

  /**
   * 确定曲线
   */
  confirmCurve() {
    // 将临时的曲线生成的模型加入到场景中
    if (
      this.tempCurveGenerateModels &&
      this.tempCurveGenerateModels.length > 0
    ) {
      let copyObjs = [];
      const ids = [];
      for (var i = 0; i < this.tempCurveGenerateModels.length; i++) {
        const obj = this.tempCurveGenerateModels[i];

        this.manager.materialManager.addNewModel(obj);
        this.manager.sceneManager.singleModels.push(obj);
        copyObjs.push(obj);

        ids.push(obj.nodeId);
        if (this.manager.isInVR())
          this.manager.vrManager.setModelComponent(obj);
      }

      this.tempCurveGenerateModels = [];

      // 前端更新结构树
      if (!this.manager.isInVR())
        store.dispatch(setTreeList(this.getMeshTree()));
      // VR端更新结构树
      else if (this.manager.isInVR()) {
        this.manager.vrManager.updateHierarchy(ids, "add");
        this.manager.vrManager.setCurrentSelectModel();
      }
    }

    this.curveUtil.clear();
  }

  _clearCurveModel() {
    if (this.tempCurveGenerateModels) {
      for (var i = 0; i < this.tempCurveGenerateModels.length; i++) {
        Util.disposeFun(this.tempCurveGenerateModels[i]);
      }
      this.tempCurveGenerateModels = [];
    }
  }

  /**
   * 在曲线上等距生成模型（临时的，需要点击“确定”按钮才会真正生成）
   * @returns
   */
  _generageModelByCurve() {
    if (this.tempCurveGenerateModels) {
      this._clearCurveModel();
    } else {
      this.tempCurveGenerateModels = [];
    }

    if (!this.currentOperateModel) return;
    if (!this.curveUtil || !this.curveUtil.line) return;

    const points = this.curveUtil.getPoints(this.modelLibArgs.dis);

    for (var i = 0; i < points.length; i++) {
      this.copyUtil.copy(this.currentOperateModel, 1, 1, 1, (obj) => {
        this.manager.sceneManager.scene.add(obj);

        // y轴旋转随机 小范围缩放随机 (幅度 0.1)
        if (this.modelLibArgs.random) {
          const angle = Math.random() * Math.PI * 2;
          obj.rotation.y = angle;
          const scale = 1 + Math.random() * this.modelRandomScaleOffset;
          obj.scale.multiplyScalar(scale);
          obj.updateMatrixWorld();

          // 靠地居中
          const box = new Box3().expandByObject(obj);
          let center = box.getCenter(new Vector3());
          const size = box.getSize(new Vector3());
          center.y -= size.y / 2;
          const offset = obj.position.clone().sub(center);
          obj.position.copy(points[i].clone().add(offset));
        } else {
          if (this.currentOperateModel._offset) {
            obj.position.copy(points[i].add(this.currentOperateModel._offset));
          } else obj.position.copy(points[i]);
        }

        this.tempCurveGenerateModels.push(obj);
      });
    }

    if (this.skyUtil) {
      let array = [].concat(this.manager.sceneManager.singleModels);
      array = array.concat(this.tempCurveGenerateModels);
      this.skyUtil.updateShadowMapSize(array);
    }
  }

  /**
   * 点击选中模型库中的模型
   * @param {*} url url
   * @param {*} url snapUrl 缩略图地址
   */
  selectModelibItem(url, snapUrl, modelId, modelName) {
    if (this.modelLibLoading) {
      if (this.manager.isInVR()) {
        message.warning("模型加载中，请稍后再试！");
      } else {
        this.manager.vrManager.makeSystemTip("模型加载中，请稍后再试！");
      }
      return;
    }
    this.modelLibLoading = true;

    const curModel = this.currentOperateModel;
    if (this.currentOperateModel) {
      this.manager.materialManager.deleteNode(this.currentOperateModel);
      this.currentOperateModel = null;
    }

    const data = {
      name: modelName,
      image: window.base + "/" + snapUrl,
      isSelect: true,
    };
    store.dispatch(setModelInfo(data));

    if (this.manager && this.manager.sceneManager) {
      this.manager.sceneManager.loadLibModel(
        url,
        modelId,
        modelName,
        (scene) => {
          console.log("设置模型库模型", scene);
          this.modelLibLoading = false;
          if (this.manager.isInVR()) {
            this.manager.vrManager.setProgress(100);
          } else if (this.loadModellibProgressCB)
            this.loadModellibProgressCB(100);
          this.currentOperateModel = scene;
          this.currentOperateModel.userData._modellibData = data;
          scene.userData.modelFrom = "modelLib"; // 模型来源为模型库，其他有 basicObj,若没有这个字段则为图纸生成的模型
          this.manager.materialManager.addNewModel(scene);
          // 加载后设置在视野之外
          this.currentOperateModel.position.y = -10000;
          if (this.modelLibArgs.modelLibType === "curve")
            this._generageModelByCurve();

          // 历史记录相关
          {
            const state = {
              oldModel: curModel,
              newModel: scene,
            };
            const cmd = new CommonCommand(
              state,
              (arg) => {
                if (this.currentOperateModel) {
                  this.manager.materialManager.deleteNode(
                    this.currentOperateModel
                  );
                  this.currentOperateModel = null;
                }
                if (arg.oldModel) {
                  this.manager.materialManager.addNewModel(arg.oldModel);
                  this.currentOperateModel = arg.oldModel;
                  this.manager.sceneManager.scene.add(this.currentOperateModel);
                  if (this.modelLibArgs.modelLibType === "curve")
                    this._generageModelByCurve();

                  store.dispatch(
                    setModelInfo(
                      this.currentOperateModel.userData._modellibData
                    )
                  );
                } else {
                  store.dispatch(
                    setModelInfo({
                      name: "",
                      image: "/defaultMat.jpg",
                      isSelect: false,
                    })
                  );
                }

                if (this.modelLibArgs.modelLibType === "curve")
                  this._generageModelByCurve();
              },
              (arg) => {
                if (this.currentOperateModel) {
                  this.manager.materialManager.deleteNode(
                    this.currentOperateModel
                  );
                  this.currentOperateModel = null;
                }
                if (arg.newModel) {
                  this.manager.materialManager.addNewModel(arg.newModel);
                  this.currentOperateModel = arg.newModel;
                  this.manager.sceneManager.scene.add(this.currentOperateModel);
                  if (this.modelLibArgs.modelLibType === "curve")
                    this._generageModelByCurve();

                  store.dispatch(
                    setModelInfo(
                      this.currentOperateModel.userData._modellibData
                    )
                  );
                } else {
                  store.dispatch(
                    setModelInfo({
                      name: "",
                      image: "/defaultMat.jpg",
                      isSelect: false,
                    })
                  );
                }

                if (this.modelLibArgs.modelLibType === "curve")
                  this._generageModelByCurve();
              }
            );
            this.manager.addHistory(cmd);
          }
        }
      );
    }
  }

  /**
   * 从服务器加载模型库数据
   * @param {*} name 查询的模型名，为空则查询所有
   * @param {*} cb 查询结果回调函数，参数为查询结果数组
   */
  loadModelData(name, cb) {
    if (!name) name = "";
    // 发起GET请求
    fetch(window.base + "/model/search?name=" + name)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP错误 ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (cb) cb(data.data);
      })
      .catch((error) => console.error("请求失败:", error));
  }

  /**
   * 点击模型库中的模型后，根据其id加载对应模型信息
   * @param {*} item
   * @param {*} cb
   */
  loadModelInfoById(item, cb) {
    // 发起GET请求
    fetch(
      `${window.base}/model/getOne?_id=${item._id}&editorRole=${item.editorRole}`
    )
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP错误 ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (cb) cb(data.data);
      })
      .catch((error) => console.error("请求失败:", error));
  }

  /**
   * 获取模型库顶层目录
   * @param {*} cb 回调函数，参数为查询结果数组
   */
  loadTopCatalog(cb) {
    const userInfo = JSON.parse(sessionStorage.getItem("userInfo"));
    const majorName = userInfo ? userInfo.major : "";

    // 发起GET请求
    fetch(window.base + "/catalog/getTopCatalog?majorName=" + majorName)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP错误 ${response.status}`);
        return response.json();
      })
      .then((data) => {
        console.log("获取模型库顶层目录", data);
        if (cb) cb(data.data);
      })
      .catch((error) => console.error("请求失败:", error));
  }

  /**
   * 获取模型库指定目录下的子目录和模型列表
   * @param {*} id 指定目录id
   * @param {*} cb 回调函数，参数为查询结果数组 type区分是模型还是目录
   * @returns
   */
  loadOneCatalog(id, cb) {
    if (!id) return;
    // 发起GET请求
    fetch(window.base + "/catalog/getOne?_id=" + id)
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP错误 ${response.status}`);
        return response.json();
      })
      .then((data) => {
        if (cb) cb(data.data);
      })
      .catch((error) => console.error("请求失败:", error));
  }

  /**
   * 记录前端面板状态变化，用于历史记录
   * @param {*} odlState
   * @param {*} newState
   */
  recordPanelStateChange(oldState, newState) {
    function symmetricCompare(a, b) {
      const keysA = Object.keys(a),
        keysB = Object.keys(b);
      const allKeys = new Set([...keysA, ...keysB]);
      if (allKeys.size !== keysA.length || allKeys.size !== keysB.length)
        return false;
      return [...allKeys].every(
        (key) =>
          a.hasOwnProperty(key) && b.hasOwnProperty(key) && a[key] === b[key]
      );
    }
    if (symmetricCompare(oldState, newState)) {
      //console.log("状态相同，不记录", oldState, newState);
    } else {
      const state = {
        oldState: oldState,
        newState: newState,
        currentOperateModel: this.currentOperateModel,
        lastMode: this.lastMode,
        currentMode: this.clickMode,
      };

      if (this.currentOperateModel) {
        if (oldState.createModelVisible && !newState.createModelVisible) {
          this.currentOperateModel.removeFromParent();
          this.currentOperateModel = null;
        } else if (oldState.editVisible && !newState.editVisible) {
          this.manager.materialManager.deleteNode(this.currentOperateModel);
          this.currentOperateModel = null;
          store.dispatch(
            setModelInfo({
              name: "",
              image: "/defaultMat.jpg",
              isSelect: false,
            })
          );
        }
      }

      const cmd = new CommonCommand(
        state,
        (state) => {
          store.dispatch(setEditVisible(state.oldState.editVisible));
          if (state.oldState.editVisible) {
            store.dispatch(
              setEditType(this.editPanelType ? this.editPanelType : "scene")
            );
          }
          store.dispatch(setTreeVisible(state.oldState.treeVisible));
          store.dispatch(setSkyVisible(state.oldState.skyVisible));
          store.dispatch(setSceneVisible(state.oldState.sceneVisible));
          store.dispatch(setViewVisible(state.oldState.viewVisible));
          store.dispatch(setVrVisible(state.oldState.vrVisible));
          store.dispatch(setMeasureVisible(state.oldState.measureVisible));
          store.dispatch(
            setCreateModelVisible(state.oldState.createModelVisible)
          );

          if (state.currentOperateModel) {
            if (
              state.oldState.createModelVisible &&
              !state.newState.createModelVisible
            ) {
              this.currentOperateModel = state.currentOperateModel;
              this.manager.sceneManager.scene.add(this.currentOperateModel);
            } else if (
              state.oldState.editVisible &&
              !state.newState.editVisible
            ) {
              this.manager.materialManager.addNewModel(
                state.currentOperateModel
              );
              this.currentOperateModel = state.currentOperateModel;
              this.manager.sceneManager.scene.add(state.currentOperateModel);
              store.dispatch(
                setModelInfo(this.currentOperateModel.userData._modellibData)
              );
            }
          }
          this.setClickMode(state.lastMode);
        },
        (state) => {
          store.dispatch(setEditVisible(state.newState.editVisible));
          if (state.newState.editVisible)
            store.dispatch(
              setEditType(this.editPanelType ? this.editPanelType : "scene")
            );
          store.dispatch(setTreeVisible(state.newState.treeVisible));
          store.dispatch(setSkyVisible(state.newState.skyVisible));
          store.dispatch(setSceneVisible(state.newState.sceneVisible));
          store.dispatch(setViewVisible(state.newState.viewVisible));
          store.dispatch(setVrVisible(state.newState.vrVisible));
          store.dispatch(setMeasureVisible(state.newState.measureVisible));
          store.dispatch(
            setCreateModelVisible(state.newState.createModelVisible)
          );
          if (state.currentOperateModel) {
            if (
              state.oldState.createModelVisible &&
              !state.newState.createModelVisible
            ) {
              this.currentOperateModel.removeFromParent();
              this.currentOperateModel = null;
            } else if (
              state.oldState.editVisible &&
              !state.newState.editVisible
            ) {
              this.manager.materialManager.deleteNode(
                state.currentOperateModel
              );
              this.currentOperateModel = null;
              store.dispatch(
                setModelInfo({
                  name: "",
                  image: "/defaultMat.jpg",
                  isSelect: false,
                })
              );
            }
          }
          this.setClickMode(state.currentMode);
        }
      );
      this.manager.addHistory(cmd);
    }
  }

  setEditPanelType(type) {
    if (!this.editPanelType) {
      this.editPanelType = "scene";
    }

    // 历史记录
    {
      const state = {
        oldState: this.editPanelType,
        newState: type,
        currentOperateModel: this.currentOperateModel,
      };
      const cmd = new CommonCommand(
        state,
        (state) => {
          store.dispatch(setEditVisible(true));
          store.dispatch(setEditType(state.oldState));
          switch (state.oldState) {
            case "scene":
              this.setClickMode(2);
              break;
            case "mountain":
              this.setClickMode(3);
              break;
            case "material":
              this.setClickMode(5);
              break;
            case "model":
              this.setClickMode(6);
              if (state.currentOperateModel) {
                this.currentOperateModel = state.currentOperateModel;
                this.manager.materialManager.restoreNode(
                  state.currentOperateModel
                );
                this.manager.sceneManager.scene.add(state.currentOperateModel);

                store.dispatch(
                  setModelInfo(this.currentOperateModel.userData._modellibData)
                );
              }
              break;
            case "road":
              this.setClickMode(4);
              break;
          }
        },
        (state) => {
          store.dispatch(setEditVisible(true));
          store.dispatch(setEditType(state.newState));
          switch (state.oldState) {
            case "scene":
              this.setClickMode(2);
              break;
            case "mountain":
              this.setClickMode(3);
              break;
            case "material":
              this.setClickMode(5);
              break;
            case "model":
              this.setClickMode(6);
              break;
            case "road":
              this.setClickMode(4);
              break;
          }
          if (state.currentOperateModel) {
            this.manager.materialManager.deleteNode(state.currentOperateModel);
            this.currentOperateModel = null;

            store.dispatch(
              setModelInfo({
                name: "",
                image: "/defaultMat.jpg",
                isSelect: false,
              })
            );
          }
        }
      );
      this.manager.addHistory(cmd);
    }

    if (this.currentOperateModel) {
      this.manager.materialManager.deleteNode(this.currentOperateModel);
      this.currentOperateModel = null;

      store.dispatch(
        setModelInfo({
          name: "",
          image: "/defaultMat.jpg",
          isSelect: false,
        })
      );
    }

    this.editPanelType = type;
  }

  /**
   * 导出gltf
   * @returns
   */
  exportGltf() {
    console.log(this.manager.sceneManager.singleModels);
    return new Promise((resolve, reject) => {
      const gltfExporter = new GLTFExporter();

      const gltfOptions = {
        trs: false, //是否只有translate，scale，rotate而没有matrix
        onlyVisible: false, //是否导出隐藏的模型
        binary: true, //是否导出二进制格式
        embedImages: true, //是否将贴图内嵌到模型
        animations: [],
      };
      gltfExporter.parse(
        this.manager.sceneManager.singleModels,
        (result) => {
          let blob;
          let gltf = result;

          if (gltf instanceof ArrayBuffer) {
            blob = Util.saveArrayBuffer(gltf);
          } else {
            const output = JSON.stringify(gltf, null, 2);
            blob = Util.saveString(output);
          }

          const name = "scene" + (gltfOptions.binary ? ".glb" : ".gltf");
          const modelData = {
            blob,
            name,
          };
          //if (!gltfOptions.embedImages) modelData.mapName = result.mapName;

          // 下载
          Util.download(modelData.blob, modelData.name);

          resolve(modelData);
        },
        gltfOptions
      );
    });
  }

}

export { Interface3D, ClickMode };
